import type { FetchApi } from '@backstage/core-plugin-api';
import type { AzureRequirements } from '@vivicta/azure-subscriptions';
import {
  DEFAULT_DOCS_PATHS,
  GITHUB_HTML_MEDIA_TYPE,
  githubApiError,
  isGitHubNotFound,
} from '@vivicta/github-config';
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import {
  ALLOWED_DATA_REGIONS_VARIABLE,
  APP_DEPLOYMENT_TASK,
  PLATFORM_DEPLOYMENT_TASK,
  AGENT_ACTIVITY_CHECK,
  APPLICATION_TOPIC,
  APPLY_SETUP_CONFIG_WORKFLOW,
  CI_WORKFLOW,
  DECOMMISSION_WORKFLOW,
  DEFAULT_DATA_REGIONS,
  DELIVERY_PLAN_LABEL,
  DEPLOY_PLATFORM_WORKFLOW,
  DEPLOY_SANDBOX_WORKFLOW,
  DEPLOY_WORKFLOW,
  ENVIRONMENT_RULES_WORKFLOW,
  IDENTITY_READY_WORKFLOW,
  IDENTITY_VENDING_WORKFLOW,
  ONBOARDING_COMPLETE_TOPIC,
  PLATFORM_RBAC_WORKFLOW,
  PROJECT_AGENT_DISPATCHER_WORKFLOW,
  REQUEST_IDENTITY_WORKFLOW,
  REQUIREMENTS_BRANCH_PREFIX,
  REQUIREMENTS_JSON_PATH,
  REQUIREMENTS_MARKDOWN_PATH,
  REQUIREMENTS_TEMPLATE_PATH,
  SETUP_CONFIG_MERGED_WORKFLOW,
  SETUP_CONFIG_PATH,
  SETUP_SETTINGS_BRANCH_PREFIX,
  TEARDOWN_SANDBOX_WORKFLOW,
  TESTS_PASSED_STATUS,
} from './repositoryContract';
import { bicepParamsFor, setBicepParams } from './infraOptions';
import { assignCreatorAsCodeOwner, CODEOWNERS_PATH } from './codeOwners';
import {
  type EnvironmentSubscriptions,
  environmentSubscriptionsInput,
} from './environments';
import type {
  InfraOptionValues,
  MergeResult,
  PullRequest,
  PullRequestComment,
  PullRequestDetail,
  PullRequestFile,
  ResolvedTemplate,
  TemplateManifest,
  WorkflowRunDetail,
} from './types';
import { renderedFileHtml } from './renderedFileHtml';
import { validateManifestStructure } from './validation';
import {
  ROLES_PATH,
  type RolesFile,
  parseRolesFile,
  rolesOf,
  serializeRolesFile,
} from '@vivicta/asdp-contract';
import {
  ASDP_JOURNEY_TOPIC,
  ASDP_PROFILE_PROPERTY,
  ASDP_STAGE_PROPERTY,
} from './asdpJourney';
import {
  CHECKPOINT_INFO,
  checkpointLabel,
  checkpointOfLabels,
} from '@vivicta/asdp-contract';
import {
  parseProfileChange,
  PROFILE_CHANGE_LABEL,
  type QuickStartChange,
  type RecordIssue,
} from './asdpRecords';
import { ASDP_SETUP_LABELS, type AsdpSetupState, asdpContract, asdpSetupState, buildAsdpSetupRequest } from './asdpOnboarding';

export type WorkflowRun = {
  id?: number;
  /** What started it, e.g. workflow_run (after CI) or workflow_dispatch. */
  event?: string;
  html_url?: string;
  /** The workflow's name, e.g. "Deploy". */
  name?: string;
  display_title?: string;
  head_commit?: { message?: string } | null;
  status?: string;
  conclusion?: string | null;
  created_at?: string;
  /** When the current attempt started; created_at stays the first attempt's. */
  run_started_at?: string;
  updated_at?: string;
  head_sha?: string;
  head_branch?: string | null;
  /** The workflow file, e.g. `.github/workflows/ci.yml`. */
  path?: string;
};

/** The newest GitHub deployment to one environment, with its latest status. */
export type EnvironmentDeployment = {
  environment: string;
  sha: string;
  ref: string;
  createdAt: string;
  creator?: string;
  /** success, failure, in_progress, queued, pending, error or inactive. */
  state?: string;
  /** Where the deployed app answers, when the workflow reported one. */
  environmentUrl?: string;
  /** The run that made the deployment. */
  logUrl?: string;
};

/** A deploy:app record: what deploy.yml or deploy-sandbox.yml shipped to an environment. */
export type AppDeployment = {
  id: number;
  environment: string;
  /** The commit the images were built from. */
  sha: string;
  createdAt: string;
  /** Who started the deploy (the record's payload), else the record's creator. */
  actor?: string;
  /** The branch a sandbox deploy came from. */
  branch?: string;
  /** success while live; inactive once replaced or torn down. */
  state?: string;
  environmentUrl?: string;
  logUrl?: string;
  /** The environment's integration tests passed for these images. */
  testsPassed: boolean;
  /** Image digests by component (backend, frontend), from the record's payload. */
  digests?: Record<string, string>;
};

export type CommitSummary = {
  sha: string;
  /** The first line of the commit message. */
  title: string;
  author?: string;
  date?: string;
};

export type CommitComparison = {
  /** Commits head has that base lacks. */
  aheadBy: number;
  /** Commits base has that head lacks. */
  behindBy: number;
  /** The commits head has that base lacks, oldest first (GitHub lists up to 250). */
  commits: CommitSummary[];
};

/** How an environment is protected, as GitHub reports it. */
export type EnvironmentProtection = {
  /** Users and teams that must approve a deployment. */
  reviewers: string[];
  preventSelfReview: boolean;
  waitTimerMinutes: number;
  /** Which branches may deploy: any, protected ones, or the listed ones. */
  branches: 'any' | 'protected' | string[];
};

export type ApplicationStage =
  | AsdpSetupState
  | 'not-started'
  | 'provisioning'
  | 'identity-ready'
  | 'setup-applied'
  | 'configured'
  | 'setup-failed'
  | 'request-failed';

export type ApplicationSummary = {
  fullName: string;
  name: string;
  description: string;
  htmlUrl: string;
  defaultBranch: string;
  updatedAt: string;
  /** When the repository was created; one deleted and created again has a new time. */
  createdAt?: string;
  /** The connected GitHub account can push to the repository. */
  canPush?: boolean;
  /** The application is on the guided journey (the asdp-journey topic). */
  onJourney?: boolean;
  stage: ApplicationStage;
  stageLabel: string;
  ciRun?: WorkflowRun;
  deployRun?: WorkflowRun;
  requestRun?: WorkflowRun;
  identityRun?: WorkflowRun;
  /** The Identity Vending Machine run that served the latest provisioning request. */
  vendingRun?: WorkflowRun;
  vendingRepository?: string;
  setupRun?: WorkflowRun;
  setupMergedRun?: WorkflowRun;
  projectAgentRun?: WorkflowRun;
};

export type RepositorySummary = {
  fullName: string;
  name: string;
  description: string;
  htmlUrl: string;
  defaultBranch: string;
  updatedAt: string;
};

export type RepositoryDocFile = {
  path: string;
  title: string;
  directory: string;
};

export type RegulationEntry = {
  slug: string;
  pkg: string;
};

export type ApplicationBootstrapInput = {
  asdpOnboardingVersion?: 1;
  /** Who can see the repository; private when absent. Internal is open to the whole enterprise. */
  repoVisibility?: 'private' | 'internal';
  projectName: string;
  projectPrefix: string;
  repoName: string;
  repoDescription: string;
  domainName: string;
  domainDescription: string;
  userRoles: string;
  additionalRegulations: string;
  riskTier: string;
  entityType: string;
  scope: string;
  piiRetentionPeriod: string;
  auditRetentionPeriod: string;
  applicableRegulations: RegulationEntry[];
  companyName: string;
  teamName: string;
  teamEmail: string;
  securityEmail: string;
  costCenter: string;
  techLead: string;
  frontendOwners: string;
  backendOwners: string;
  platformOwners: string;
  securityOwners: string;
  complianceOwners: string;
  sonarProjectKey: string;
  sonarOrganization: string;
  opaBundleVersion: string;
  region: string;
  tenantId: string;
  nonprodSubscriptionId: string;
  prodSubscriptionId: string;
  /** Subscription per environment; nonprod/prodSubscriptionId follow from it. Absent for older applications. */
  environmentSubscriptions?: EnvironmentSubscriptions;
  entraAppClientId: string;
  entraAdminGroupObjectId: string;
  vnetResourceGroup: string;
  vnetName: string;
  acrLoginServer: string;
  githubOrg: string;
  templateOwner: string;
  templateRepo: string;
  environments: string[];
  /**
   * Values of the template's `spec.infraOptions`, keyed by option id.
   * Written into `infra/parameters/*.bicepparam` when the repository is
   * created; absent for applications created before templates had options.
   */
  infraOptions?: InfraOptionValues;
  /**
   * Application ID (UUID) the IVM tags every Azure resource with; the
   * decommission workflow deletes only what carries it. Assigned at creation;
   * absent for applications created before it existed.
   */
  appId?: string;
};

export type DecommissionOptions = {
  /** Delete the Azure resources the IVM created for the application. */
  deleteAzure: boolean;
  /** Delete the GitHub repository (after the Azure resources, when both are chosen). */
  deleteRepository: boolean;
  /** Only plan: verify ownership and list what would be deleted. */
  dryRun: boolean;
  /** Fingerprint from a completed dry-run plan, required for deletion. */
  expectedPlanHash?: string;
  /**
   * With deleteAzure: only these environments' resources (shared, sandbox,
   * dev, test, prod). Absent means all of them.
   */
  environments?: string[];
};

/** One of the application's resource groups, as the plan's inventory lists it. */
export type DecommissionInventoryGroup = {
  id: string;
  name: string;
  environment: string;
  /** "identity" for an environment's identity group, "app" otherwise. */
  purpose: string;
};

/** What the IVM's decommission workflow will delete (its decommission-plan annotation). */
export type DecommissionPlan = {
  plan_hash?: string;
  resource_group_ids?: string[];
  app_registrations?: Array<{ displayName: string; appId: string }>;
  resource_groups: string[];
  role_assignments: Array<{ role: string; scope: string }>;
  federated_credentials: Array<{ name: string; subject: string }>;
  delete_repository: boolean;
  /** False when the Azure resources are kept; absent from older IVM plans, where they were always deleted. */
  delete_azure?: boolean;
  repo_exists: boolean;
  dry_run: boolean;
  /** Same repository, other or no app ID: left alone. */
  untagged: string[];
  /** The environments the plan deletes; absent from IVM plans before environments existed. */
  environments?: string[];
  /** True when some environments are kept. */
  partial?: boolean;
  /** Every resource group of the application, deleted or not. */
  inventory?: DecommissionInventoryGroup[];
};

export type DecommissionStep = {
  name: string;
  status?: string;
  conclusion?: string | null;
};

export type DecommissionJob = {
  name: string;
  status?: string;
  conclusion?: string | null;
  startedAt?: string;
  completedAt?: string;
  /** The job's page on GitHub. */
  url?: string;
  /** The job's own steps, without the runner's set-up, post and clean-up steps. */
  steps?: DecommissionStep[];
};

export type ResourceGroupDeletionState =
  | 'waiting'
  | 'deleting'
  | 'deleted'
  /** Already deleted when the run reached it. */
  | 'gone'
  | 'failed';

export type ResourceGroupDeletion = {
  id: string;
  name: string;
  state: ResourceGroupDeletionState;
  resourcesAtStart?: number | null;
  resourcesLeft?: number | null;
  /** Up to five names of the resources still in the group. */
  remaining?: string[];
  startedAt?: string | null;
  completedAt?: string | null;
};

/** Live resource group deletion, published by the IVM while it deletes. */
export type ResourceGroupDeletionProgress = {
  updatedAt?: string;
  groups: ResourceGroupDeletion[];
};

export type DecommissionProgress = {
  run: WorkflowRun;
  jobs: DecommissionJob[];
  plan?: DecommissionPlan;
  /** Absent before the IVM starts deleting groups, and for runs of IVM versions that don't publish it. */
  deletion?: ResourceGroupDeletionProgress;
  /** Why the IVM refused, when the plan job failed with a reason. */
  refusal?: string;
  /**
   * The plan job finished but its annotations couldn't be read (the portal's
   * GitHub App needs Checks: read); the run link is the only feedback.
   */
  planUnavailable?: boolean;
};

/**
 * The user cancelled creating an application. With `repoFullName`, the
 * repository exists (with its saved setup input, so it can be deleted).
 */
export class CreationCancelledError extends Error {
  constructor(readonly repoFullName?: string) {
    super('Creating the application was cancelled.');
    this.name = 'CreationCancelledError';
  }
}

/** What createApplication is doing, reported as it goes. */
export type CreationStep =
  | 'repository'
  | 'template'
  | 'topics'
  | 'code-owner'
  | 'bootstrap-input'
  | 'requirements'
  | 'infra-options'
  | 'identity-request';

/** What createRepository is doing (journey spec §3.1.1 steps 1–3). */
export type JourneyCreateStep = 'repository' | 'template' | 'visibility' | 'settings' | 'properties';

export type CreatedJourneyRepository = {
  repoFullName: string;
  defaultBranch: string;
  creator: string;
  /** The input as saved in the repository, prod removed. */
  input: ApplicationBootstrapInput;
  /** Set when the start-stage properties couldn't be written. */
  propertiesNote?: string;
  /** Set when the repository couldn't be made internal and stayed private. */
  visibilityNote?: string;
};

export type ContainerLogLine = {
  time: string;
  stream: string;
  revision: string;
  message: string;
};

export type LogApp = 'frontend' | 'backend';

/** One app's latest log lines in an environment, or why the portal can't read them. */
export type ContainerLogs =
  | {
      available: true;
      resourceGroup: string;
      /** The revision serving traffic; lines from others predate the last deploy. */
      activeRevision?: string;
      lines: ContainerLogLine[];
    }
  | { available: false; reason: string };

export type DeployedResource = {
  type: string;
  name: string;
  state: 'ready' | 'deploying' | 'failed';
};

/** Live Bicep progress of one IVM Provision job, published by the IVM. */
export type DeploymentProgress = {
  label: string;
  state?: string;
  updatedAt?: string;
  resources: DeployedResource[];
};

/** One step the agent reported, e.g. 12:04:31 "Writing docs/requirements.md". */
export type AgentActivityEvent = { time: string; message: string };

/** What the agent has done so far in a run, from invoke-agent.yml's check run. */
export type AgentActivity = {
  /** The latest progress line, e.g. "Running" or "Failed: …". */
  summary?: string;
  completed: boolean;
  events: AgentActivityEvent[];
};

const AGENT_ACTIVITY_LINE = /^- `(\d{2}:\d{2}:\d{2})` (.+)$/;

/** The check run's text, one `- \`HH:MM:SS\` message` line per step; other lines are ignored. */
export const parseAgentActivityText = (
  text?: string | null,
): AgentActivityEvent[] =>
  (text ?? '').split('\n').flatMap(line => {
    const match = AGENT_ACTIVITY_LINE.exec(line.trim());
    return match ? [{ time: match[1], message: match[2] }] : [];
  });

export type ProvisioningProgress = {
  run: WorkflowRun;
  jobs: DecommissionJob[];
  /** By environment label (shared, dev, …), for Provision jobs that are deploying. */
  deployments: Record<string, DeploymentProgress>;
};

export const PROVISION_JOB_PATTERN = /^Provision \((.+)\)$/;

export const DECOMMISSION_PLAN_JOB = 'Verify Ownership and Plan';
export const DECOMMISSION_DELETE_JOB = 'Delete Non-Prod Resources';
/** The IVM's check run with live deletion progress, named `decommission-progress/<run ID>`. */
const DECOMMISSION_PROGRESS_CHECK = 'decommission-progress';
/** The IVM's check runs with live Bicep progress, named `identity-progress/<run ID>/<environment>`. */
const IDENTITY_PROGRESS_CHECK = 'identity-progress';

/** A deletion run (not a plan-only run) that hasn't finished yet. */
export type ActiveDecommission = {
  runId: number;
  requestId: string;
  requestedAt: number;
  status?: string;
  htmlUrl?: string;
};

/** Recent IVM decommission runs searched for an application's unfinished one. */
const ACTIVE_DECOMMISSION_RUN_SCAN = 30;

/** The application's latest identity request, for re-running it from Settings. */
export type IdentityRequestStatus = {
  requestRun?: WorkflowRun;
  /** The request or the IVM run answering it hasn't finished. */
  inProgress: boolean;
  /** GitHub can still re-run the request run with its original inputs. */
  rerunnable: boolean;
};

/** GitHub re-runs a workflow run only within 30 days of its first attempt. */
const RERUN_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const isRunnerStep = (name: string) =>
  !name ||
  name === 'Set up job' ||
  name === 'Complete job' ||
  name.startsWith('Post ') ||
  // The runner pre-pulling a Docker action's image, e.g. "Pull owner/image:tag@sha256:…".
  /^Pull \S+[:@]/.test(name);

/** A run's jobs with their own steps, without the runner's bookkeeping steps. */
const toRunJobs = (response: WorkflowRunJobsResponse): DecommissionJob[] =>
  (response.jobs ?? []).map(job => ({
    name: job.name,
    status: job.status,
    conclusion: job.conclusion,
    startedAt: job.started_at ?? undefined,
    completedAt: job.completed_at ?? undefined,
    url: job.html_url ?? undefined,
    steps: (job.steps ?? [])
      .map(step => ({
        name: step.name ?? '',
        status: step.status,
        conclusion: step.conclusion,
      }))
      .filter(step => !isRunnerStep(step.name)),
  }));

type SearchResponse = {
  items: Array<{
    name: string;
    full_name: string;
    description: string | null;
    html_url: string;
    created_at?: string;
    updated_at: string;
    default_branch: string;
    topics?: string[];
    /** The requesting user's access; reads go out with their token. */
    permissions?: { push?: boolean };
  }>;
};

type WorkflowRunsResponse = {
  total_count?: number;
  workflow_runs?: WorkflowRun[];
};

/** The newest runs of a repository across all workflows; `complete` when that is its whole history. */
type RecentRuns = { runs: WorkflowRun[]; complete: boolean };

type WorkflowRunJobsResponse = {
  jobs?: Array<{
    id: number;
    name: string;
    status?: string;
    conclusion?: string | null;
    check_run_url?: string;
    html_url?: string | null;
    started_at?: string | null;
    completed_at?: string | null;
    steps?: Array<{
      name?: string;
      status?: string;
      conclusion?: string | null;
    }>;
  }>;
};

type CheckRunAnnotation = {
  annotation_level?: string;
  title?: string | null;
  message?: string;
};

export type CheckRunCounts = { running: number; failed: number };

export type ReviewEvent = 'APPROVE' | 'REQUEST_CHANGES' | 'COMMENT';

/** A pull request review, as GitHub returns it. */
export type PullRequestReview = {
  id: number;
  user: { login: string; type?: string; avatar_url?: string } | null;
  state: string;
  commit_id: string;
  body: string | null;
  submitted_at?: string;
};

/** A commit of a pull request: who wrote it and who committed it. */
export type PullRequestCommit = {
  sha: string;
  author: { login: string; type?: string } | null;
  committer: { login: string; type?: string } | null;
  commit: { committer?: { date?: string } | null };
};

/** A line comment in a review; `start_line` makes it a range. */
export type ReviewLineComment = {
  path: string;
  body: string;
  line: number;
  start_line?: number;
  side: 'RIGHT';
  start_side?: 'RIGHT';
};

const ENABLE_AUTO_MERGE_MUTATION = `mutation EnableAutoMerge($pullRequestId: ID!, $mergeMethod: PullRequestMergeMethod!) {
  enablePullRequestAutoMerge(input: { pullRequestId: $pullRequestId, mergeMethod: $mergeMethod }) {
    pullRequest { number autoMergeRequest { enabledAt } }
  }
}`;

const FAILED_CHECK_CONCLUSIONS = new Set([
  'failure',
  'timed_out',
  'cancelled',
  'action_required',
  'startup_failure',
]);

type CheckRunsResponse = {
  check_runs?: Array<{
    external_id?: string | null;
    status?: string;
    output?: { summary?: string | null; text?: string | null };
  }>;
};

const RESOURCE_STATES = new Set<string>(['ready', 'deploying', 'failed']);

/** The IVM's deployment progress JSON, or undefined when it isn't one it understands. */
export const parseDeploymentProgress = (
  text?: string | null,
): DeploymentProgress | undefined => {
  if (!text) return undefined;
  try {
    const parsed = JSON.parse(text) as {
      version?: number;
      kind?: string;
      label?: string;
      state?: string;
      updatedAt?: string;
      resources?: unknown;
    };
    if (
      parsed.version !== 1 ||
      parsed.kind !== 'deployment' ||
      typeof parsed.label !== 'string' ||
      !Array.isArray(parsed.resources)
    ) {
      return undefined;
    }
    return {
      label: parsed.label,
      state: parsed.state,
      updatedAt: parsed.updatedAt,
      resources: (parsed.resources as DeployedResource[]).filter(
        resource =>
          typeof resource?.name === 'string' &&
          typeof resource.type === 'string' &&
          RESOURCE_STATES.has(resource.state),
      ),
    };
  } catch {
    return undefined;
  }
};

const DELETION_STATES = new Set<string>([
  'waiting',
  'deleting',
  'deleted',
  'gone',
  'failed',
]);

/** The progress JSON of the IVM's check run, or undefined when it isn't one it understands. */
export const parseResourceGroupDeletion = (
  text?: string | null,
): ResourceGroupDeletionProgress | undefined => {
  if (!text) return undefined;
  try {
    const parsed = JSON.parse(text) as {
      version?: number;
      updatedAt?: string;
      groups?: unknown;
    };
    if (parsed.version !== 1 || !Array.isArray(parsed.groups)) return undefined;
    const groups = (parsed.groups as ResourceGroupDeletion[]).filter(
      group =>
        typeof group?.id === 'string' &&
        typeof group.name === 'string' &&
        DELETION_STATES.has(group.state),
    );
    return { updatedAt: parsed.updatedAt, groups };
  } catch {
    return undefined;
  }
};

type CheckRunResponse = {
  output?: {
    title?: string;
    summary?: string;
    text?: string;
  };
};

// The runner adds this to every failed step; it says nothing about the cause.
const GENERIC_EXIT_ANNOTATION = /^Process completed with exit code \d+\.?$/;

type WorkflowsResponse = {
  workflows?: Array<{
    path?: string;
  }>;
};

type RepoResponse = {
  full_name: string;
  html_url: string;
  default_branch: string;
  visibility?: string;
};

type WorkflowDispatchRequest = {
  ref: string;
  inputs?: Record<string, string>;
};

type FileResponse = {
  sha: string;
  content: string;
  encoding: string;
};

type DirectoryEntryResponse = Array<{
  name: string;
  path: string;
  type: string;
}>;

type GitTreeResponse = {
  sha: string;
  tree: Array<{
    path: string;
    type: 'blob' | 'tree' | 'commit';
  }>;
  truncated: boolean;
};

const REPO_BOOTSTRAP_TIMEOUT_MS = 120_000;
const CACHE_TTL_MS = 60_000;
const TEMPLATE_CACHE_TTL_MS = 300_000; // 5 minutes for template discovery
const REPO_BOOTSTRAP_POLL_MS = 3_000;
/** GitHub locks a repository for a short while after a visibility change. */
const REPO_LOCKED = /repository has been locked/i;
const REPO_LOCK_TIMEOUT_MS = 60_000;
const RECENT_RUNS_PER_PAGE = 50;
/**
 * Environments an application may have been deployed to and still be deleted
 * from the portal: everything but production. ci is the CI pipeline, not an
 * application environment. The IVM's
 * scripts/decommission/repo-deployed-only-to-dev.sh enforces the same list.
 */
const DELETABLE_DEPLOYMENT_ENVIRONMENTS: ReadonlySet<string> = new Set([
  'ci',
  'sandbox',
  'dev',
  'test',
]);
/** Deployment states that mean it went live: inactive is a success a later deployment replaced. */
const LIVE_DEPLOYMENT_STATES: ReadonlySet<string> = new Set([
  'success',
  'inactive',
]);
const DEPLOYMENTS_PAGE_SIZE = 100;
/** The CI job's environment gates the pipeline; nothing is deployed to it. */
const NON_APPLICATION_ENVIRONMENTS: ReadonlySet<string> = new Set(['ci']);
const MAX_DEPLOYMENT_PAGES = 10;
const REQUEST_IDENTITY_WORKFLOW_PATH = `.github/workflows/${REQUEST_IDENTITY_WORKFLOW}`;
const PORTAL_BOOTSTRAP_INPUT_PATH = '.vivicta/bootstrap-input.json';
const TEMPLATE_MANIFEST_PATH = '.vivicta/template-manifest.yaml';
/** The template's deployment values: infra/deploy.sh and the Bicep parameters read the prefix and location from it. */
const AZURE_SETTINGS_PATH = '.vivicta/azure.json';
/** What a template's platform needs registered on a subscription; the IVM registers both. */
const TEMPLATE_PROVIDERS_PATH = 'infra/resource-providers.txt';
const TEMPLATE_FEATURES_PATH = 'infra/azure-features.txt';
const BICEP_PARAMETERS_DIR = 'infra/parameters';
const TEMPLATE_DISCOVERY_TOPIC = 'vivicta-template';

const getFileName = (path: string) => path.split('/').pop() ?? path;

const isRunOf = (run: WorkflowRun, workflowFileName: string) =>
  Boolean(run.path) &&
  getFileName(run.path!.split('@')[0]) === workflowFileName;

/**
 * The latest run of a workflow as getLatestWorkflowRun reads it, from a page
 * of recent runs. Undefined when the page can't tell: an older run may sit
 * beyond it.
 */
const findRecentRun = (
  recent: RecentRuns,
  workflowFileName: string,
  branch?: string,
): { run?: WorkflowRun } | undefined => {
  const runs = recent.runs.filter(
    run =>
      isRunOf(run, workflowFileName) && (!branch || run.head_branch === branch),
  );
  const meaningful = runs.find(run => run.conclusion !== 'skipped');
  return meaningful || recent.complete
    ? { run: meaningful ?? runs[0] }
    : undefined;
};

/** Inputs of the template's request-identity.yml for an application. */
export const identityRequestInputs = (
  input: Pick<
    ApplicationBootstrapInput,
    | 'projectPrefix'
    | 'nonprodSubscriptionId'
    | 'prodSubscriptionId'
    | 'environmentSubscriptions'
    | 'environments'
    | 'appId'
  >,
): Record<string, string> => {
  const ownSubscriptions = environmentSubscriptionsInput(input);
  // Templates reject inputs they don't declare, so each is sent only when used.
  return {
    project_prefix: input.projectPrefix,
    nonprod_subscription_id: input.nonprodSubscriptionId,
    ...(input.environments.includes('prod')
      ? { prod_subscription_id: input.prodSubscriptionId }
      : {}),
    ...(ownSubscriptions
      ? { environment_subscriptions: ownSubscriptions }
      : {}),
    environments: ['ci', ...input.environments].join(','),
    ...(input.appId ? { app_id: input.appId } : {}),
  };
};

const encodeBase64Utf8 = (value: string) => {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCodePoint(byte);
  }
  return btoa(binary);
};

const decodeBase64Utf8 = (value: string) => {
  const normalized = value.replaceAll('\n', '');
  const binary = atob(normalized);
  const bytes = Uint8Array.from(
    binary,
    character => character.codePointAt(0) ?? 0,
  );
  return new TextDecoder().decode(bytes);
};

/** A one-entry-per-line list file's entries, without blank lines and # comments. */
export const listedLines = (content: string) =>
  content
    .split('\n')
    .map(line => line.trim())
    .filter(line => line && !line.startsWith('#'));

/** A payload field that maps names to strings, e.g. the image digests by component. */
const stringRecord = (value: unknown): Record<string, string> | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  const entries = Object.entries(value).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

const normalizeFailureMessage = (value: string) =>
  value
    .replaceAll('\r', '')
    .replaceAll('`', '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .filter(line => !line.startsWith('##'))
    .join(' ')
    .replaceAll(/\s+/g, ' ')
    .trim();

const parseCheckRunId = (checkRunUrl?: string) => {
  if (!checkRunUrl) {
    return undefined;
  }

  const match = /\/check-runs\/(\d+)/.exec(checkRunUrl);
  if (!match) {
    return undefined;
  }

  return Number(match[1]);
};

/** Rounds without an IVM run after which a cancelled request is taken to have sent none. */
const QUIET_ROUNDS_WITHOUT_IVM_RUN = 2;

const delay = (milliseconds: number) =>
  new Promise<void>(resolve => {
    globalThis.setTimeout(resolve, milliseconds);
  });

/** Leaves out blank values: apply-setup-config.yml skips them, and the schema rejects many as empty strings. */
const withoutBlanks = (
  section: Record<string, string | undefined>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(section)
      .map(([key, value]) => [key, value?.trim() ?? ''] as const)
      .filter(([, value]) => value !== ''),
  );

export const buildSetupConfig = (input: ApplicationBootstrapInput) => {
  const teamName = input.teamName || `${input.repoName}-team`;

  return stringifyYaml({
    version: '1.0',
    ready_to_apply: true,
    project: withoutBlanks({
      name: input.projectName,
      prefix: input.projectPrefix,
    }),
    github: withoutBlanks({ org: input.githubOrg, repo: input.repoName }),
    domain: withoutBlanks({
      name: input.domainName,
      description: input.domainDescription,
      user_roles: input.userRoles,
      additional_regulations: input.additionalRegulations,
    }),
    compliance: {
      ...withoutBlanks({
        risk_tier: input.riskTier,
        entity_type: input.entityType,
        scope: input.scope,
        pii_retention_period: input.piiRetentionPeriod,
        audit_log_retention_period: input.auditRetentionPeriod,
      }),
      applicable_regulations: (input.applicableRegulations ?? []).map(item => ({
        slug: item.slug,
        package: item.pkg,
      })),
    },
    organization: withoutBlanks({
      company_name: input.companyName,
      team_name: teamName,
      team_email: input.teamEmail,
      security_email: input.securityEmail,
      cost_center: input.costCenter,
    }),
    codeowners: withoutBlanks({
      tech_lead: input.techLead,
      frontend: input.frontendOwners,
      backend: input.backendOwners,
      platform: input.platformOwners,
      security: input.securityOwners,
      compliance: input.complianceOwners,
    }),
    tooling: withoutBlanks({
      sonar_project_key: input.sonarProjectKey,
      sonar_organization: input.sonarOrganization,
      opa_bundle_version: input.opaBundleVersion,
    }),
    identity: { mode: 'vending' },
    azure: withoutBlanks({
      region: input.region,
      tenant_id: input.tenantId,
      nonprod_subscription_id: input.nonprodSubscriptionId,
      prod_subscription_id: input.prodSubscriptionId,
      entra_app_client_id: input.entraAppClientId,
      entra_admin_group_object_id: input.entraAdminGroupObjectId,
      vnet_resource_group: input.vnetResourceGroup,
      vnet_name: input.vnetName,
      acr_login_server: input.acrLoginServer,
    }),
  });
};

export const buildRequirementsTemplate = (
  input: ApplicationBootstrapInput,
) => `# Business Requirement Document: ${input.projectName}

## 0. Administrative Information

- **Organization Name:** ${input.companyName}
- **Business Unit:** ${input.domainName}
- **Team Name:** ${input.teamName || `${input.repoName}-team`}
- **Cost Center:** ${input.costCenter}
- **Project Risk Tier:** ${input.riskTier}

---

## 1. Executive Summary

- **Project Overview:** ${input.domainDescription}
- **Objectives:** Describe the business outcomes for ${input.projectName}.
- **Business Value:** Capture expected impact, efficiency gains, or revenue outcomes.

## 2. Governance & Stakeholders

| Role | Name / Department | Responsibility |
| :--- | :---------------- | :------------- |
| Project Owner | [Name] | Outcome ownership |
| Compliance Officer | [Name] | Regulatory sign-off |
| Tech Lead | [Name] | Solution direction |
| Subject Matter Expert | [Name] | Domain knowledge |

## 3. Regulatory & Compliance Requirements

Applicable regulations: ${
  input.applicableRegulations.map(item => item.slug).join(', ') ||
  '[Add applicable regulations]'
}

## 4. Functional Requirements

- **FR-01:** Define the core user workflow for ${input.projectName}.
- **FR-02:** Define key operational or compliance automation needs.
- **FR-03:** Define reporting, approvals, or audit expectations.

## 5. Non-Functional Requirements

- **Availability:** [Target uptime]
- **Latency:** [Target response time]
- **Data Residency:** [Region or jurisdiction constraints]

## 6. Security Requirements

- **Identity Management:** Tenant ${input.tenantId}
- **Audit Logs:** Retain for ${
  input.auditRetentionPeriod || '[Define retention]'
}
- **PII Retention:** ${input.piiRetentionPeriod || '[Define retention]'}

## 7. Data Requirements

- **Primary user roles:** ${input.userRoles}
- **Additional regulations:** ${
  input.additionalRegulations || '[None provided]'
}

## 8. User Experience (UX)

- **Accessibility:** [WCAG expectations]
- **Collaboration:** [How Product Owners, Compliance, and Engineering collaborate]

## 9. Acceptance Criteria

- [Define acceptance criteria for launch readiness]
`;

const buildBootstrapInputJson = (input: ApplicationBootstrapInput) =>
  `${JSON.stringify(input, null, 2)}\n`;

/**
 * The template's azure.json with Start's prefix, region and application name,
 * keeping the template's other values; undefined when it isn't a JSON object.
 */
export const withStartAzureSettings = (
  azureJson: string,
  input: Pick<ApplicationBootstrapInput, 'projectPrefix' | 'region' | 'projectName'>,
) => {
  let settings: unknown;
  try {
    settings = JSON.parse(azureJson);
  } catch {
    return undefined;
  }
  if (typeof settings !== 'object' || settings === null || Array.isArray(settings)) return undefined;
  return `${JSON.stringify(
    {
      ...settings,
      prefix: input.projectPrefix,
      location: input.region,
      projectDisplayName: input.projectName,
    },
    null,
    2,
  )}\n`;
};

const FAILED_RUN_CONCLUSIONS = new Set([
  'failure',
  'cancelled',
  'timed_out',
  'startup_failure',
]);

export const isFailedRun = (run?: WorkflowRun) =>
  FAILED_RUN_CONCLUSIONS.has(run?.conclusion ?? '');

const FAILED_STEP_CONCLUSIONS = new Set([
  ...FAILED_RUN_CONCLUSIONS,
  'action_required',
]);

export type WorkflowRunFailure = {
  job?: string;
  step?: string;
  /** Normalised check-run output, when the job published any. */
  details?: string;
};

export const describeWorkflowRunFailure = (failure?: WorkflowRunFailure) => {
  if (failure?.details) {
    return failure.details;
  }
  if (failure?.step) {
    return `${failure.job}: ${failure.step}`;
  }
  return failure?.job ? `Failed at job: ${failure.job}` : undefined;
};

/** An identity callback older than the latest provisioning request belongs to a previous attempt. */
export const isIdentityRunCurrent = (
  requestRun?: WorkflowRun,
  identityRun?: WorkflowRun,
) =>
  !requestRun?.created_at ||
  !identityRun?.created_at ||
  identityRun.created_at >= requestRun.created_at;

/** True while the outcome lives only in the IVM repository: the request was sent, no callback yet. */
const isAwaitingIdentityCallback = (
  requestRun?: WorkflowRun,
  identityRun?: WorkflowRun,
) =>
  requestRun?.conclusion === 'success' &&
  !(isIdentityRunCurrent(requestRun, identityRun) && identityRun?.status);

/** IVM run titles are `Identity Request by Repo: <repo name or owner/repo>`. */
const isVendingRunFor = (run: WorkflowRun, repoFullName: string) => {
  const subject = run.display_title?.split(': ').pop()?.trim().toLowerCase();
  const fullName = repoFullName.toLowerCase();
  return subject === fullName || subject === fullName.split('/')[1];
};

const deriveStage = (
  requestRun?: WorkflowRun,
  identityRun?: WorkflowRun,
  setupRun?: WorkflowRun,
  setupMergedRun?: WorkflowRun,
  vendingRun?: WorkflowRun,
  onboardingFinished = false,
) => {
  // Project is fully configured only when the setup PR has been merged
  // (setup-config-merged.yml sets PROJECT_READY=true)
  if (setupMergedRun?.conclusion === 'success') {
    return { stage: 'configured' as const, stageLabel: 'Configured' };
  }

  // The owner finished onboarding from the portal (finishOnboarding)
  if (onboardingFinished) {
    return { stage: 'configured' as const, stageLabel: 'Onboarding finished' };
  }

  // apply-setup-config created a PR but it hasn't been merged yet
  if (setupRun?.conclusion === 'success') {
    return {
      stage: 'setup-applied' as const,
      stageLabel: 'Setup PR ready for review',
    };
  }

  if (setupRun?.conclusion === 'failure') {
    return {
      stage: 'setup-failed' as const,
      stageLabel: 'Setup needs reapply',
    };
  }

  // apply-setup-config is queued or running — treat as setup-applied so the
  // stepper stays at step 5 instead of regressing to an earlier step.
  if (setupRun?.status === 'queued' || setupRun?.status === 'in_progress') {
    return {
      stage: 'setup-applied' as const,
      stageLabel: 'Applying configuration…',
    };
  }

  if (
    identityRun?.status === 'queued' ||
    identityRun?.status === 'in_progress'
  ) {
    return {
      stage: 'provisioning' as const,
      stageLabel: 'Provisioning identities',
    };
  }

  const currentIdentityRun = isIdentityRunCurrent(requestRun, identityRun)
    ? identityRun
    : undefined;

  if (currentIdentityRun?.conclusion === 'success') {
    return { stage: 'identity-ready' as const, stageLabel: 'Identity ready' };
  }

  if (isFailedRun(currentIdentityRun)) {
    return {
      stage: 'request-failed' as const,
      stageLabel: 'Identity callback failed',
    };
  }

  if (isFailedRun(requestRun)) {
    return {
      stage: 'request-failed' as const,
      stageLabel: 'Provisioning request failed',
    };
  }

  if (isFailedRun(vendingRun)) {
    return {
      stage: 'request-failed' as const,
      stageLabel: 'Identity Vending Machine failed',
    };
  }

  if (vendingRun?.conclusion === 'success') {
    return {
      stage: 'provisioning' as const,
      stageLabel: 'Waiting for identity callback',
    };
  }

  if (requestRun?.status) {
    return {
      stage: 'provisioning' as const,
      stageLabel: 'Provisioning requested',
    };
  }

  return { stage: 'not-started' as const, stageLabel: 'Not started' };
};

const isMarkdownFile = (path: string) => /\.(md|mdx)$/i.test(path);

const toDocumentTitle = (path: string) => {
  const fileName = path.split('/').pop() ?? path;
  return fileName
    .replace(/\.(md|mdx)$/i, '')
    .replaceAll(/[-_]+/g, ' ')
    .replaceAll(/\b\w/g, character => character.toUpperCase());
};

/** A function that returns the user's GitHub OAuth token on demand. */
export type GitHubTokenProvider = () => Promise<string>;

export class GitHubPortalApi {
  private userTokenProvider?: GitHubTokenProvider;
  private readonly catalogRegistrations = new Set<string>();
  private readonly cache = new Map<
    string,
    { data: unknown; expiresAt: number }
  >();

  constructor(
    private readonly applicationPortalBaseUrl: string,
    private readonly fetchApi: FetchApi,
    private readonly vendingRepository?: string,
  ) {}

  static create(
    fetchApi: FetchApi,
    applicationPortalBaseUrl: string,
    vendingRepository?: string,
  ) {
    return new GitHubPortalApi(
      applicationPortalBaseUrl,
      fetchApi,
      vendingRepository,
    );
  }

  /** Set the provider used to acquire the user's GitHub OAuth token. */
  setUserTokenProvider(provider: GitHubTokenProvider) {
    this.userTokenProvider = provider;
  }

  private getCached<T>(key: string): T | undefined {
    const entry = this.cache.get(key);
    if (entry && entry.expiresAt > Date.now()) {
      return entry.data as T;
    }
    this.cache.delete(key);
    return undefined;
  }

  private setCache(key: string, data: unknown) {
    this.cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
  }

  private async request<T>(
    path: string,
    init?: RequestInit & { expectText?: boolean },
  ) {
    const lockDeadline = Date.now() + REPO_LOCK_TIMEOUT_MS;
    let response: Response;
    for (;;) {
      // eslint-disable-next-line no-await-in-loop -- retried only while the repository is locked
      response = await this.fetchApi.fetch(
        `${this.applicationPortalBaseUrl}${path}`,
        {
          ...init,
          headers: {
            'Content-Type': 'application/json',
            ...init?.headers,
          },
        },
      );
      if (response.ok) break;

      // eslint-disable-next-line no-await-in-loop
      const errorBody = await response.text();
      if (!REPO_LOCKED.test(errorBody) || Date.now() >= lockDeadline) {
        throw githubApiError(response.status, errorBody, 'GitHub request failed');
      }
      // eslint-disable-next-line no-await-in-loop -- intentional polling delay between retries
      await delay(REPO_BOOTSTRAP_POLL_MS);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    if (init?.expectText) {
      return (await response.text()) as T;
    }

    return (await response.json()) as T;
  }

  private async getLatestWorkflowRun(
    repoFullName: string,
    workflowFileName: string,
    availableWorkflows?: ReadonlySet<string>,
    /** Only runs on this branch, e.g. CI on the default branch, not pull requests. */
    branch?: string,
  ) {
    if (availableWorkflows && !availableWorkflows.has(workflowFileName)) {
      return undefined;
    }

    try {
      // Fetch a small batch so we can skip "skipped" runs — those indicate
      // the workflow's `if` conditions were not met (e.g. template-sync PRs
      // triggering setup-config-merged.yml) and are not meaningful for stage
      // derivation.
      const params = new URLSearchParams({ per_page: '5' });
      if (branch) params.set('branch', branch);
      const response = await this.request<WorkflowRunsResponse>(
        `/repos/${repoFullName}/actions/workflows/${encodeURIComponent(
          workflowFileName,
        )}/runs?${params.toString()}`,
      );
      return (
        response.workflow_runs?.find(r => r.conclusion !== 'skipped') ??
        response.workflow_runs?.[0]
      );
    } catch {
      return undefined;
    }
  }

  /**
   * setup-config-merged.yml runs on every pull request merged to main and
   * skips all but the setup PR, so after a few more merges its successful run
   * drops out of the batch getLatestWorkflowRun reads. Asks for the latest
   * success first; without one, the latest meaningful run (a failure, say).
   */
  private async getSetupMergedRun(
    repoFullName: string,
    availableWorkflows?: ReadonlySet<string>,
  ) {
    if (
      availableWorkflows &&
      !availableWorkflows.has(SETUP_CONFIG_MERGED_WORKFLOW)
    ) {
      return undefined;
    }

    try {
      const response = await this.request<WorkflowRunsResponse>(
        `/repos/${repoFullName}/actions/workflows/${encodeURIComponent(
          SETUP_CONFIG_MERGED_WORKFLOW,
        )}/runs?status=success&per_page=1`,
      );
      const successfulRun = response.workflow_runs?.[0];
      if (successfulRun) {
        return successfulRun;
      }
    } catch {
      // Fall back to the latest run below.
    }

    return this.getLatestWorkflowRun(
      repoFullName,
      SETUP_CONFIG_MERGED_WORKFLOW,
      availableWorkflows,
    );
  }

  private async getRecentRuns(
    repoFullName: string,
  ): Promise<RecentRuns | undefined> {
    try {
      const response = await this.request<WorkflowRunsResponse>(
        `/repos/${repoFullName}/actions/runs?per_page=${RECENT_RUNS_PER_PAGE}`,
      );
      const runs = response.workflow_runs ?? [];
      return {
        runs,
        complete:
          response.total_count !== undefined &&
          response.total_count <= runs.length,
      };
    } catch {
      return undefined;
    }
  }

  /**
   * The runs an application summary reads. One page of recent runs answers
   * most of them; a workflow whose latest run is older gets its own request.
   */
  private async getApplicationRuns(
    repoFullName: string,
    defaultBranch?: string,
  ) {
    const recent = await this.getRecentRuns(repoFullName);
    let availableWorkflows: Promise<Set<string>> | undefined;
    const workflowFiles = () =>
      (availableWorkflows ??= this.getAvailableWorkflowFiles(repoFullName));

    const latestRun = async (workflowFileName: string, branch?: string) => {
      const known = recent && findRecentRun(recent, workflowFileName, branch);
      return known
        ? known.run
        : this.getLatestWorkflowRun(
            repoFullName,
            workflowFileName,
            await workflowFiles(),
            branch,
          );
    };

    const readSetupMergedRun = async () => {
      const success = recent?.runs.find(
        run =>
          isRunOf(run, SETUP_CONFIG_MERGED_WORKFLOW) &&
          run.conclusion === 'success',
      );
      if (success) {
        return success;
      }
      const known = recent?.complete
        ? findRecentRun(recent, SETUP_CONFIG_MERGED_WORKFLOW)
        : undefined;
      return known
        ? known.run
        : this.getSetupMergedRun(repoFullName, await workflowFiles());
    };

    const [
      requestRun,
      identityRun,
      setupRun,
      setupMergedRun,
      ciRun,
      deployRun,
      projectAgentRun,
    ] = await Promise.all([
      latestRun(REQUEST_IDENTITY_WORKFLOW),
      latestRun(IDENTITY_READY_WORKFLOW),
      latestRun(APPLY_SETUP_CONFIG_WORKFLOW),
      readSetupMergedRun(),
      // What reaches the environments is built on the default branch;
      // pull request runs would read as the application's state.
      latestRun(CI_WORKFLOW, defaultBranch),
      latestRun(DEPLOY_WORKFLOW),
      latestRun(PROJECT_AGENT_DISPATCHER_WORKFLOW),
    ]);

    return {
      requestRun,
      identityRun,
      setupRun,
      setupMergedRun,
      ciRun,
      deployRun,
      projectAgentRun,
    };
  }

  private async getRepo(repoFullName: string) {
    return this.request<RepoResponse>(`/repos/${repoFullName}`);
  }

  /**
   * The IVM only calls back on success, so a failure there is invisible from
   * the application repository. Undefined when the run can't be read.
   */
  /** The IVM run answering this repository's identity request, once it exists. */
  async getVendingRun(repoFullName: string, requestRun: WorkflowRun) {
    return this.findVendingRun(repoFullName, requestRun);
  }

  private async findVendingRun(repoFullName: string, requestRun: WorkflowRun) {
    if (!this.vendingRepository || !requestRun.created_at) {
      return undefined;
    }

    // No `created=>=…`: the gateway's firewall blocks it (SQL operator rule)
    const params = new URLSearchParams({
      event: 'repository_dispatch',
      per_page: '50',
    });
    const requestedAt = Date.parse(requestRun.created_at);

    try {
      const response = await this.request<WorkflowRunsResponse>(
        `/repos/${
          this.vendingRepository
        }/actions/workflows/${encodeURIComponent(
          IDENTITY_VENDING_WORKFLOW,
        )}/runs?${params.toString()}`,
      );
      return response.workflow_runs?.find(
        run =>
          isVendingRunFor(run, repoFullName) &&
          Date.parse(run.created_at ?? '') >= requestedAt,
      );
    } catch {
      return undefined;
    }
  }

  private async getAvailableWorkflowFiles(repoFullName: string) {
    try {
      const response = await this.request<WorkflowsResponse>(
        `/repos/${repoFullName}/actions/workflows?per_page=100`,
      );

      return new Set(
        (response.workflows ?? [])
          .map(workflow => workflow.path)
          .filter((value): value is string => Boolean(value))
          .map(getFileName),
      );
    } catch {
      return new Set<string>();
    }
  }

  private async getDirectoryEntries(
    repoFullName: string,
    path: string,
    ref?: string,
  ) {
    const query = ref ? `?ref=${encodeURIComponent(ref)}` : '';

    try {
      return await this.request<DirectoryEntryResponse>(
        `/repos/${repoFullName}/contents/${path}${query}`,
      );
    } catch (error) {
      if (isGitHubNotFound(error)) {
        return undefined;
      }

      throw error;
    }
  }

  private async getRequirementsPath(
    repoFullName: string,
    ref?: string,
    options?: { skipTemplate?: boolean },
  ) {
    const docsEntries = await this.getDirectoryEntries(
      repoFullName,
      'docs',
      ref,
    );
    if (!docsEntries?.length) {
      return undefined;
    }

    const docsFiles = new Set(
      docsEntries
        .filter(entry => entry.type === 'file')
        .map(entry => entry.name),
    );
    if (docsFiles.has('requirements.json')) {
      return REQUIREMENTS_JSON_PATH;
    }
    if (!options?.skipTemplate && docsFiles.has('requirements-template.md')) {
      return REQUIREMENTS_TEMPLATE_PATH;
    }
    if (docsFiles.has('requirements.md')) {
      return REQUIREMENTS_MARKDOWN_PATH;
    }

    return undefined;
  }

  private async waitForRepoBootstrap(repoFullName: string, ref: string, workflowPath = REQUEST_IDENTITY_WORKFLOW_PATH) {
    const deadline = Date.now() + REPO_BOOTSTRAP_TIMEOUT_MS;

    while (Date.now() < deadline) {
      try {
        await this.getRepo(repoFullName);
        const workflowFile = await this.getFile(
          repoFullName,
          workflowPath,
          ref,
        );
        if (workflowFile) {
          return;
        }
      } catch {
        // The repository can be returned from the template-generation API before
        // GitHub has finished materializing the default branch and workflow files.
      }

      // eslint-disable-next-line no-await-in-loop -- intentional polling delay between retries
      await delay(REPO_BOOTSTRAP_POLL_MS);
    }

    throw new Error(
      `Repository ${repoFullName} was created, but GitHub is still generating template contents. Refresh and retry provisioning in a minute.`,
    );
  }

  /** Applications in one org, or in any of several. */
  async listApplications(org: string | readonly string[]) {
    const orgs = (typeof org === 'string' ? [org] : org)
      .map(name => `org:${name}`)
      .join(' ');
    const params = new URLSearchParams({
      q: `${orgs} topic:${APPLICATION_TOPIC} archived:false`,
      per_page: '50',
      sort: 'updated',
      order: 'desc',
    });

    const search = await this.request<SearchResponse>(
      `/search/repositories?${params.toString()}`,
    );

    const applications = await Promise.all(
      search.items.map(async item => {
        if (item.topics?.includes('vivicta-asdp')) {
          const setup = await this.getAsdpSetup(item.full_name);
          const summary: ApplicationSummary = {
            fullName: item.full_name, name: item.name, description: item.description ?? '',
            htmlUrl: item.html_url, defaultBranch: item.default_branch || 'main',
            updatedAt: item.updated_at, createdAt: item.created_at,
            canPush: item.permissions?.push === true,
            onJourney: item.topics?.includes(ASDP_JOURNEY_TOPIC) === true,
            stage: setup.status,
            stageLabel: ASDP_SETUP_LABELS[setup.status],
            setupRun: setup.run,
          };
          return summary;
        }
        return this.summarizeApplication(item);
      }),
    );

    this.registerSetUpApplications(applications);
    return applications;
  }

  /**
   * Marks an application's onboarding as finished, so it leaves the onboarding
   * list and appears under Applications without the setup PR. Keeps the
   * repository's other topics.
   */
  async finishOnboarding(repoFullName: string) {
    const { names = [] } = await this.request<{ names?: string[] }>(
      `/repos/${repoFullName}/topics`,
    );
    if (names.includes(ONBOARDING_COMPLETE_TOPIC)) {
      return;
    }
    await this.request(`/repos/${repoFullName}/topics`, {
      method: 'PUT',
      body: JSON.stringify({ names: [...names, ONBOARDING_COMPLETE_TOPIC] }),
    });
  }

  /** One repository by `owner/name`, e.g. a shared repository outside the portal's org. */
  /** One application from its repository, with the identity and setup runs that give its stage. */
  async getApplicationSummary(repoFullName: string): Promise<ApplicationSummary> {
    return this.summarizeApplication(
      await this.request<SearchResponse['items'][number]>(`/repos/${repoFullName}`),
    );
  }

  private async summarizeApplication(item: SearchResponse['items'][number]): Promise<ApplicationSummary> {
    const {
      requestRun,
      identityRun,
      setupRun,
      setupMergedRun,
      ciRun,
      deployRun,
      projectAgentRun,
    } = await this.getApplicationRuns(
      item.full_name,
      item.default_branch || undefined,
    );

    const vendingRun =
      requestRun && isAwaitingIdentityCallback(requestRun, identityRun)
        ? await this.findVendingRun(item.full_name, requestRun)
        : undefined;

    const derived = deriveStage(
      requestRun,
      identityRun,
      setupRun,
      setupMergedRun,
      vendingRun,
      item.topics?.includes(ONBOARDING_COMPLETE_TOPIC),
    );

    return {
      fullName: item.full_name,
      name: item.name,
      description: item.description ?? '',
      htmlUrl: item.html_url,
      defaultBranch: item.default_branch || 'main',
      updatedAt: item.updated_at,
      createdAt: item.created_at,
      canPush: item.permissions?.push === true,
      onJourney: item.topics?.includes(ASDP_JOURNEY_TOPIC) === true,
      ciRun,
      deployRun,
      requestRun,
      identityRun,
      vendingRun,
      vendingRepository: vendingRun ? this.vendingRepository : undefined,
      setupRun,
      setupMergedRun,
      projectAgentRun,
      ...derived,
    } satisfies ApplicationSummary;
  }

  async getRepository(repoFullName: string): Promise<RepositorySummary> {
    const cacheKey = `repo:${repoFullName}`;
    const cached = this.getCached<RepositorySummary>(cacheKey);
    if (cached) {
      return cached;
    }

    const repo = await this.request<SearchResponse['items'][number]>(
      `/repos/${repoFullName}`,
    );
    const result = {
      fullName: repo.full_name,
      name: repo.name,
      description: repo.description ?? '',
      htmlUrl: repo.html_url,
      defaultBranch: repo.default_branch || 'main',
      updatedAt: repo.updated_at,
    } satisfies RepositorySummary;

    this.setCache(cacheKey, result);
    return result;
  }

  async listRepositories(org: string) {
    const cacheKey = `repos:${org}`;
    const cached = this.getCached<RepositorySummary[]>(cacheKey);
    if (cached) {
      return cached;
    }

    const params = new URLSearchParams({
      q: `org:${org} archived:false`,
      per_page: '100',
      sort: 'updated',
      order: 'desc',
    });

    const search = await this.request<SearchResponse>(
      `/search/repositories?${params.toString()}`,
    );

    const result = search.items.map(item => ({
      fullName: item.full_name,
      name: item.name,
      description: item.description ?? '',
      htmlUrl: item.html_url,
      defaultBranch: item.default_branch || 'main',
      updatedAt: item.updated_at,
    })) satisfies RepositorySummary[];

    this.setCache(cacheKey, result);
    return result;
  }

  async listRepositoryDocs(
    repoFullName: string,
    ref?: string,
    paths: readonly string[] = DEFAULT_DOCS_PATHS,
  ): Promise<RepositoryDocFile[]> {
    const cacheKey = `docs:${repoFullName}:${ref ?? ''}:${paths.join(',')}`;
    const cached = this.getCached<RepositoryDocFile[]>(cacheKey);
    if (cached) {
      return cached;
    }

    let files: string[];

    try {
      const treeRef = ref || 'HEAD';
      const treeResponse = await this.request<GitTreeResponse>(
        `/repos/${repoFullName}/git/trees/${encodeURIComponent(
          treeRef,
        )}?recursive=1`,
      );

      if (treeResponse.truncated) {
        files = await this.collectMarkdownDocsIn(repoFullName, paths, ref);
      } else {
        files = treeResponse.tree
          .filter(
            entry =>
              entry.type === 'blob' &&
              paths.some(path => entry.path.startsWith(`${path}/`)) &&
              isMarkdownFile(entry.path),
          )
          .map(entry => entry.path);
      }
    } catch {
      files = await this.collectMarkdownDocsIn(repoFullName, paths, ref);
    }

    const result = files
      .toSorted((left, right) => left.localeCompare(right))
      .map(path => ({
        path,
        title: toDocumentTitle(path),
        directory: path.includes('/')
          ? path.slice(0, path.lastIndexOf('/'))
          : '',
      }));

    this.setCache(cacheKey, result);
    return result;
  }

  private async collectMarkdownDocsIn(
    repoFullName: string,
    paths: readonly string[],
    ref?: string,
  ): Promise<string[]> {
    const perPath = await Promise.all(
      paths.map(path => this.collectMarkdownDocs(repoFullName, path, ref)),
    );
    return perPath.flat();
  }

  private async collectMarkdownDocs(
    repoFullName: string,
    path: string,
    ref?: string,
  ): Promise<string[]> {
    const entries = await this.getDirectoryEntries(repoFullName, path, ref);
    if (!entries?.length) {
      return [];
    }

    const directories = entries
      .filter(entry => entry.type === 'dir')
      .map(entry => entry.path)
      .sort((left, right) => left.localeCompare(right));

    const files = entries
      .filter(entry => entry.type === 'file' && isMarkdownFile(entry.path))
      .map(entry => entry.path)
      .sort((left, right) => left.localeCompare(right));

    const nestedFiles = await Promise.all(
      directories.map(directoryPath =>
        this.collectMarkdownDocs(repoFullName, directoryPath, ref),
      ),
    );

    return [...files, ...nestedFiles.flat()];
  }

  private async getFile(repoFullName: string, path: string, ref?: string) {
    const query = ref ? `?ref=${encodeURIComponent(ref)}` : '';

    try {
      const response = await this.request<FileResponse>(
        `/repos/${repoFullName}/contents/${path}${query}`,
      );
      if (response.encoding !== 'base64') {
        throw new Error(`Unsupported encoding for ${path}`);
      }

      return {
        sha: response.sha,
        content: decodeBase64Utf8(response.content),
      };
    } catch (error) {
      if (isGitHubNotFound(error)) {
        return undefined;
      }

      throw error;
    }
  }

  private async upsertFile(options: {
    repoFullName: string;
    path: string;
    branch: string;
    content: string;
    message: string;
  }) {
    const existing = await this.getFile(
      options.repoFullName,
      options.path,
      options.branch,
    );

    return this.request(
      `/repos/${options.repoFullName}/contents/${options.path}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          message: options.message,
          content: encodeBase64Utf8(options.content),
          branch: options.branch,
          sha: existing?.sha,
        }),
      },
    );
  }

  /** Writes the chosen infrastructure options into the new repository's Bicep parameter files. */
  private async applyInfraOptions(
    repoFullName: string,
    branch: string,
    input: ApplicationBootstrapInput,
  ) {
    if (!input.infraOptions) return;
    const manifest = await this.getTemplateManifest(
      `${input.templateOwner}/${input.templateRepo}`,
    );
    const params = bicepParamsFor(
      manifest?.spec.infraOptions ?? [],
      input.infraOptions,
    );
    if (Object.keys(params).length === 0) return;

    const entries = await this.request<{ name: string; path: string }[]>(
      `/repos/${repoFullName}/contents/${BICEP_PARAMETERS_DIR}?ref=${encodeURIComponent(
        branch,
      )}`,
    ).catch(error => {
      if (isGitHubNotFound(error)) return [];
      throw error;
    });

    const updates = await Promise.all(
      entries
        .filter(item => item.name.endsWith('.bicepparam'))
        .map(async entry => {
          const file = await this.getFile(repoFullName, entry.path, branch);
          if (!file) return undefined;
          const content = setBicepParams(file.content, params);
          return content === file.content
            ? undefined
            : { path: entry.path, content };
        }),
    );

    // One at a time: every write moves the branch head, so parallel writes conflict.
    await updates.reduce<Promise<unknown>>(
      (previous, update) =>
        update
          ? previous.then(() =>
              this.upsertFile({
                repoFullName,
                branch,
                path: update.path,
                content: update.content,
                message:
                  'chore(portal): apply infrastructure options [ai-assisted]',
              }),
            )
          : previous,
      Promise.resolve(),
    );
  }

  /**
   * Creates the repository from the template and prepares it. `signal`
   * cancels: before the repository exists nothing is created; once it exists,
   * creation goes on until the setup input (with the application ID that
   * deleting it needs) is saved, then stops.
   */
  async createApplication(
    applicationInput: ApplicationBootstrapInput,
    onStep: (step: CreationStep) => void = () => undefined,
    signal?: AbortSignal,
  ) {
    const stopIfCancelled = (repoFullName?: string) => {
      if (signal?.aborted) throw new CreationCancelledError(repoFullName);
    };
    stopIfCancelled();
    const contract = asdpContract(await this.getTemplateManifest(
      `${applicationInput.templateOwner}/${applicationInput.templateRepo}`,
    ));
    if (contract) buildAsdpSetupRequest(applicationInput, 1);
    onStep('repository');
    const { login: creator } = await this.request<{ login: string }>('/user');
    const input = {
      ...applicationInput,
      ...(contract && { asdpOnboardingVersion: 1 as const }),
      appId: applicationInput.appId ?? crypto.randomUUID(),
      techLead: applicationInput.techLead || `@${creator}`,
    };
    const createdRepo = await this.request<RepoResponse>(
      `/repos/${input.templateOwner}/${input.templateRepo}/generate`,
      {
        method: 'POST',
        body: JSON.stringify({
          owner: input.githubOrg,
          name: input.repoName,
          description: input.repoDescription,
          private: true,
          include_all_branches: false,
        }),
      },
    );

    const defaultBranch = createdRepo.default_branch || 'main';
    const repoFullName = createdRepo.full_name;

    onStep('template');
    await this.waitForRepoBootstrap(repoFullName, defaultBranch,
      contract ? `.github/workflows/${contract.workflow}` : REQUEST_IDENTITY_WORKFLOW_PATH);

    onStep('topics');
    await this.request(`/repos/${repoFullName}/topics`, {
      method: 'PUT',
      body: JSON.stringify({
        names: [APPLICATION_TOPIC, 'vivicta-sdlc-template', ...(contract ? ['vivicta-asdp'] : [])],
      }),
    });

    onStep('code-owner');
    const codeOwners = await this.getFile(
      repoFullName,
      CODEOWNERS_PATH,
      defaultBranch,
    );
    if (codeOwners && !contract) {
      const content = assignCreatorAsCodeOwner(codeOwners.content, creator);
      if (content !== codeOwners.content) {
        await this.upsertFile({
          repoFullName,
          branch: defaultBranch,
          path: CODEOWNERS_PATH,
          content,
          message:
            'chore(portal): make the creator the code owner [ai-assisted]',
        });
      }
    }

    onStep('bootstrap-input');
    await this.upsertFile({
      repoFullName,
      branch: defaultBranch,
      path: PORTAL_BOOTSTRAP_INPUT_PATH,
      content: buildBootstrapInputJson(input),
      message: 'chore(portal): persist bootstrap input [ai-assisted]',
    });

    stopIfCancelled(repoFullName);
    if (contract) {
      await this.requestAsdpSetup(repoFullName, signal);
      return { repoFullName, defaultBranch, input };
    }
    onStep('requirements');
    await this.upsertFile({
      repoFullName,
      branch: defaultBranch,
      path: REQUIREMENTS_TEMPLATE_PATH,
      content: buildRequirementsTemplate(input),
      message:
        'docs(requirements): personalize requirements template [ai-assisted]',
    });

    stopIfCancelled(repoFullName);
    onStep('infra-options');
    await this.applyInfraOptions(repoFullName, defaultBranch, input);
    stopIfCancelled(repoFullName);

    return { repoFullName, defaultBranch, input };
  }

  /**
   * Asks the IVM to provision the application's identities. Separate from
   * createApplication so a failed dispatch leaves a created repository the
   * user can resume, not one the wizard forgets.
   */
  async requestIdentityProvisioning(
    repoFullName: string,
    defaultBranch: string,
    input: ApplicationBootstrapInput,
  ) {
    const identityRequest = {
      ref: defaultBranch,
      inputs: identityRequestInputs(input),
    };
    await this.dispatchWorkflow(
      repoFullName,
      REQUEST_IDENTITY_WORKFLOW,
      identityRequest,
    ).catch(async error => {
      if (!isGitHubNotFound(error)) {
        throw error;
      }

      await this.waitForRepoBootstrap(repoFullName, defaultBranch);
      await this.dispatchWorkflow(
        repoFullName,
        REQUEST_IDENTITY_WORKFLOW,
        identityRequest,
      );
    });
  }

  /** Starts provisioning again from the input saved in the repository when it was created. */
  async resumeIdentityProvisioning(repoFullName: string) {
    const repo = await this.getRepo(repoFullName);
    const file = await this.getFile(
      repoFullName,
      PORTAL_BOOTSTRAP_INPUT_PATH,
      repo.default_branch,
    );
    if (!file) {
      throw new Error(
        `${repoFullName} has no saved setup input (${PORTAL_BOOTSTRAP_INPUT_PATH}), so provisioning can't be resumed from the portal.`,
      );
    }
    const input = JSON.parse(file.content) as ApplicationBootstrapInput;
    await this.requestIdentityProvisioning(repoFullName, repo.default_branch, {
      ...input,
      environments: input.environments ?? [],
    });
  }

  // ── ASDP journey: Start (journey spec §3.1.1) ─────────────────────

  /**
   * Steps 1–3 of Start: generate the repository (private, then internal when
   * asked), tag it, commit roles.yaml with the creator as Project Manager, the
   * bootstrap input, azure.json and the infrastructure options, then set the start-stage
   * custom properties. The properties must stay the last direct write: the
   * start-stage ruleset applies from that moment. A refused property write
   * (no gate App yet) doesn't stop Start; `propertiesNote` says why.
   */
  async createRepository(
    bootstrapInput: ApplicationBootstrapInput,
    onStep: (step: JourneyCreateStep) => void = () => undefined,
    signal?: AbortSignal,
  ): Promise<CreatedJourneyRepository> {
    if (signal?.aborted) throw new CreationCancelledError();
    onStep('repository');
    const { login: creator } = await this.request<{ login: string }>('/user');
    const input: ApplicationBootstrapInput = {
      ...bootstrapInput,
      environments: bootstrapInput.environments.filter(env => env !== 'prod'),
      prodSubscriptionId: '',
      appId: bootstrapInput.appId ?? crypto.randomUUID(),
      techLead: bootstrapInput.techLead || `@${creator}`,
    };
    const created = await this.request<RepoResponse>(
      `/repos/${input.templateOwner}/${input.templateRepo}/generate`,
      {
        method: 'POST',
        body: JSON.stringify({
          owner: input.githubOrg,
          name: input.repoName,
          description: input.repoDescription,
          private: true,
          include_all_branches: false,
        }),
      },
    );
    const repoFullName = created.full_name;
    const defaultBranch = created.default_branch || 'main';
    if (signal?.aborted) throw new CreationCancelledError(repoFullName);

    onStep('template');
    await this.request(`/repos/${repoFullName}/topics`, {
      method: 'PUT',
      body: JSON.stringify({ names: [APPLICATION_TOPIC, ASDP_JOURNEY_TOPIC] }),
    });
    await this.waitForRepoBootstrap(repoFullName, defaultBranch, TEMPLATE_MANIFEST_PATH);
    // First, so a Create that stops later still has its app ID: deleting it
    // through the IVM, and the journey, both read it from this file.
    await this.upsertFile({
      repoFullName,
      branch: defaultBranch,
      path: PORTAL_BOOTSTRAP_INPUT_PATH,
      content: buildBootstrapInputJson(input),
      message: 'chore(portal): persist bootstrap input [ai-assisted]',
    });

    // Generating from a template only knows private or public; internal is set
    // once GitHub has finished generating: it answered 422 straight after generate.
    let visibilityNote: string | undefined;
    if (input.repoVisibility === 'internal') onStep('visibility');
    if (input.repoVisibility === 'internal' && created.visibility !== 'internal') {
      try {
        await this.request(`/repos/${repoFullName}`, {
          method: 'PATCH',
          body: JSON.stringify({ visibility: 'internal' }),
        });
      } catch (error) {
        const reason = error instanceof Error ? ` ${error.message.replace(/\.?$/, '.')}` : '';
        visibilityNote = `${repoFullName} stayed private: it couldn't be made internal.${reason} An organization owner can change it under Settings.`;
      }
    }
    onStep('settings');
    await this.upsertFile({
      repoFullName,
      branch: defaultBranch,
      path: ROLES_PATH,
      content: serializeRolesFile({
        schema_version: 1,
        roles: { project_manager: [creator] },
      }),
      message: 'chore(asdp): name the creator as Project Manager [ai-assisted]',
    });
    await this.writeStartAzureSettings(repoFullName, defaultBranch, input);
    // Such as the AI platform chosen on Start.
    await this.applyInfraOptions(repoFullName, defaultBranch, input);

    onStep('properties');
    let propertiesNote: string | undefined;
    try {
      await this.setStartStageProperties(repoFullName);
    } catch (error) {
      // GitHub's own words, without the request the portal made.
      const reason = error instanceof Error ? ` (${error.message.split(': ').at(-1)?.replace(/\.$/, '')})` : '';
      propertiesNote = `GitHub didn't let the portal set ${ASDP_STAGE_PROPERTY} and ${ASDP_PROFILE_PROPERTY}${reason}, so the main branch isn't protected yet. Everything else works; an organization owner can set them later.`;
    }
    return { repoFullName, defaultBranch, creator, input, propertiesNote, visibilityNote };
  }

  /**
   * Puts Start's prefix, region and name into the template's azure.json, so
   * its deployment needs no pull request for them. A template without the
   * file deploys some other way and is left as it is.
   */
  private async writeStartAzureSettings(
    repoFullName: string,
    branch: string,
    input: ApplicationBootstrapInput,
  ) {
    const file = await this.getFile(repoFullName, AZURE_SETTINGS_PATH, branch);
    const content = file && withStartAzureSettings(file.content, input);
    if (!content) return;
    await this.upsertFile({
      repoFullName,
      branch,
      path: AZURE_SETTINGS_PATH,
      content,
      message: 'chore(portal): set the Azure prefix, region and name from Start [ai-assisted]',
    });
  }

  /**
   * The last direct write of Start: from here the start-stage ruleset applies.
   * The backend sets the properties only on an application without a stage,
   * so this can't move a classified one back to quick start.
   */
  private async setStartStageProperties(repoFullName: string) {
    if (!this.userTokenProvider) {
      throw new Error('Sign in with GitHub to start the application.');
    }
    const response = await this.fetchApi.fetch(
      `${this.applicationPortalBaseUrl}/asdp/apps/${encodeURIComponent(repoFullName)}/stage/start`,
      {
        method: 'POST',
        headers: { 'X-GitHub-Token': await this.userTokenProvider() },
      },
    );
    if (!response.ok) {
      throw githubApiError(response.status, await response.text(), 'The portal refused the start stage');
    }
  }

  /**
   * Step 5 of Start: the non-prod identity, sandbox and dev (plus test when
   * chosen) and never prod, which only the Gate 4 decision adds.
   */
  async requestNonProdIdentity(
    repoFullName: string,
    defaultBranch: string,
    input: ApplicationBootstrapInput,
  ) {
    await this.requestIdentityProvisioning(repoFullName, defaultBranch, {
      ...input,
      environments: input.environments.filter(env => env !== 'prod'),
      prodSubscriptionId: '',
    });
  }

  /** Whether a repository is on the ASDP journey (topic `asdp-journey`). */
  async isAsdpJourney(repoFullName: string): Promise<boolean> {
    const { names } = await this.request<{ names?: string[] }>(
      `/repos/${repoFullName}/topics`,
    );
    return (names ?? []).includes(ASDP_JOURNEY_TOPIC);
  }

  /**
   * GitHub people matching `query`, for the roles editor. Needs the backend
   * to allow `GET /search/users`; callers fall back to typed logins.
   */
  async searchUsers(query: string): Promise<string[]> {
    const params = new URLSearchParams({ q: `${query} type:user`, per_page: '8' });
    const result = await this.request<{ items?: Array<{ login: string }> }>(
      `/search/users?${params.toString()}`,
    );
    return (result.items ?? []).map(item => item.login);
  }

  /**
   * roles.yaml of the user's most recently updated other journey application
   * that names them, for "Add my usual team"; undefined when there is none.
   */
  async findUsualTeam(
    org: string,
    login: string,
    excludeRepo: string,
  ): Promise<{ repo: string; roles: RolesFile } | undefined> {
    const params = new URLSearchParams({
      q: `org:${org} topic:${ASDP_JOURNEY_TOPIC} archived:false`,
      per_page: '10',
      sort: 'updated',
      order: 'desc',
    });
    const search = await this.request<SearchResponse>(
      `/search/repositories?${params.toString()}`,
    );
    for (const item of search.items) {
      if (item.full_name.toLowerCase() === excludeRepo.toLowerCase()) continue;
      const file = await this.getFile(item.full_name, ROLES_PATH).catch(() => undefined);
      if (!file) continue;
      try {
        const roles = parseRolesFile(file.content);
        if (rolesOf(roles.roles, login).length > 0) {
          return { repo: item.full_name, roles };
        }
      } catch {
        // A malformed roles.yaml can't be copied; look at the next application.
      }
    }
    return undefined;
  }

  async dispatchWorkflow(
    repoFullName: string,
    workflowFileName: string,
    payload: WorkflowDispatchRequest,
  ) {
    await this.request(
      `/repos/${repoFullName}/actions/workflows/${encodeURIComponent(
        workflowFileName,
      )}/dispatches`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
  }

  async requestAsdpSetup(repoFullName: string, signal?: AbortSignal) {
    const stopIfCancelled = () => {
      if (signal?.aborted) throw new CreationCancelledError(repoFullName);
    };
    stopIfCancelled();
    const repository = await this.getRepo(repoFullName);
    const contract = asdpContract(await this.getTemplateManifest(repoFullName));
    if (!contract) throw new Error('This repository does not use ASDP onboarding.');
    if (await this.getFile(repoFullName, contract.requestPath, repository.default_branch)) {
      throw new Error('Configuration is already adopted. Use a reviewed change instead of initial setup.');
    }
    const bootstrap = await this.getFile(repoFullName, PORTAL_BOOTSTRAP_INPUT_PATH, repository.default_branch);
    if (!bootstrap) throw new Error('The saved ASDP form is missing.');
    const input: unknown = JSON.parse(bootstrap.content);
    buildAsdpSetupRequest(input, 1);
    stopIfCancelled();
    const issue = await this.request<{ number: number }>(`/repos/${repoFullName}/issues`, {
      method: 'POST',
      body: JSON.stringify({ title: 'Initial ASDP configuration',
        body: 'Review the proposed classification inputs, roles and autonomy. No approvals or agent activation are granted by initial setup.' }),
    });
    stopIfCancelled();
    await this.dispatchWorkflow(repoFullName, contract.workflow, {
      ref: repository.default_branch,
      inputs: { request: JSON.stringify(buildAsdpSetupRequest(input, issue.number)) },
    });
  }

  async getAsdpSetup(repoFullName: string) {
    const repository = await this.getRepo(repoFullName);
    const contract = asdpContract(await this.getTemplateManifest(repoFullName));
    if (!contract) throw new Error('This repository does not use ASDP onboarding.');
    const pulls = await this.request<PullRequest[]>(`/repos/${repoFullName}/pulls?state=open&per_page=100`);
    const pullRequest = pulls.find(pull => pull.head.ref.startsWith('sdlc/setup-'));
    const merged = await this.getFile(repoFullName, contract.requestPath, repository.default_branch);
    const ref = merged ? repository.default_branch : pullRequest?.head.ref;
    const control = ref ? await this.getFile(repoFullName, contract.controlPath, ref) : undefined;
    const effective = control ? JSON.parse(control.content) as { profile: string; autonomy: Record<string, string> } : undefined;
    const run = await this.getLatestWorkflowRun(repoFullName, contract.workflow);
    return { status: asdpSetupState(merged ?? pullRequest, Boolean(merged)), pullRequest, run,
      profile: effective?.profile, autonomy: effective?.autonomy ?? {} };
  }

  /**
   * Whether the project agent has created the delivery plan: any issue,
   * open or closed, labelled as one of its epics. Undefined when GitHub
   * couldn't be read.
   */
  async hasDeliveryPlan(repoFullName: string): Promise<boolean | undefined> {
    try {
      const response = await this.request<Array<{ id: number }>>(
        `/repos/${repoFullName}/issues?labels=${encodeURIComponent(
          DELIVERY_PLAN_LABEL,
        )}&state=all&per_page=1`,
      );
      return response.length > 0;
    } catch {
      return undefined;
    }
  }

  async hasIssuesWithLabel(
    repoFullName: string,
    label: string,
  ): Promise<boolean> {
    try {
      const response = await this.request<Array<{ id: number }>>(
        `/repos/${repoFullName}/issues?labels=${encodeURIComponent(
          label,
        )}&state=open&per_page=1`,
      );
      return Array.isArray(response) && response.length > 0;
    } catch {
      return false;
    }
  }

  async getRequirementsDocument(
    repoFullName: string,
  ): Promise<{ content: string; format: 'json' | 'markdown' }> {
    const requirementsPath = await this.getRequirementsPath(repoFullName);
    if (!requirementsPath) {
      return { content: '', format: 'markdown' };
    }

    const file = await this.getFile(repoFullName, requirementsPath);
    if (!file) {
      return { content: '', format: 'markdown' };
    }

    return {
      content: file.content,
      format: requirementsPath.endsWith('.json') ? 'json' : 'markdown',
    };
  }

  async hasRequirementsDocument(repoFullName: string, ref?: string) {
    const requirementsPath = await this.getRequirementsPath(repoFullName, ref, {
      skipTemplate: true,
    });
    return Boolean(requirementsPath);
  }

  /**
   * The file rendered to HTML by GitHub, or `undefined` when it doesn't exist.
   * Rendering is a GET by path: posting the Markdown text to `/markdown` puts
   * code samples in a request body, which the Application Gateway WAF blocks
   * as SQL/command injection.
   */
  async renderFile(repoFullName: string, path: string, ref?: string) {
    const query = ref ? `?ref=${encodeURIComponent(ref)}` : '';

    try {
      const html = await this.request<string>(
        `/repos/${repoFullName}/contents/${path}${query}`,
        { expectText: true, headers: { Accept: GITHUB_HTML_MEDIA_TYPE } },
      );
      return renderedFileHtml(html);
    } catch (error) {
      if (isGitHubNotFound(error)) {
        return undefined;
      }

      throw error;
    }
  }

  /**
   * Starts setup: commits setup-config.yaml, filled in from what was chosen in
   * the portal. apply-setup-config.yml runs on that push and opens the setup
   * pull request. The requirement agent runs later, once setup is merged.
   */
  async applySetupConfig(
    repoFullName: string,
    input: ApplicationBootstrapInput,
  ) {
    const repo = await this.getRepo(repoFullName);
    await this.upsertFile({
      repoFullName,
      branch: repo.default_branch,
      path: SETUP_CONFIG_PATH,
      content: buildSetupConfig(input),
      message: 'chore(setup): apply setup-config.yaml [ai-assisted]',
    });
  }

  async reapplySetupConfig(repoFullName: string) {
    const repo = await this.getRepo(repoFullName);
    const current = await this.getFile(
      repoFullName,
      SETUP_CONFIG_PATH,
      repo.default_branch,
    );

    if (!current) {
      throw new Error('setup-config.yaml was not found in the repository');
    }

    // Ensure ready_to_apply is true so the workflow proceeds
    const updated = current.content.replace(
      /^ready_to_apply:\s*false$/m,
      'ready_to_apply: true',
    );

    await this.upsertFile({
      repoFullName,
      branch: repo.default_branch,
      path: SETUP_CONFIG_PATH,
      content: updated,
      message: 'chore(setup): reapply setup-config.yaml [ai-assisted]',
    });
  }

  /**
   * Starts the IVM's decommission workflow for an application. The workflow
   * verifies the app ID, repository ID and tags, then waits for approval on
   * its `decommission` environment before deleting anything.
   */
  async decommissionApplication(
    repoFullName: string,
    options: DecommissionOptions,
  ) {
    if (!this.vendingRepository) {
      throw new Error(
        'The Identity Vending Machine repository is not configured.',
      );
    }
    if (!options.deleteAzure && !options.deleteRepository) {
      throw new Error(
        'Choose what to delete: the repository, the Azure resources or both.',
      );
    }
    if (
      !options.dryRun &&
      !/^[0-9a-f]{64}$/.test(options.expectedPlanHash ?? '')
    ) {
      throw new Error(
        'Generate and review a deletion plan before requesting approval.',
      );
    }
    const blocking = await this.liveEnvironmentsBlockingDeletion(repoFullName);
    if (blocking.length > 0) {
      throw new Error(
        `${repoFullName} has been deployed to ${blocking.join(
          ', ',
        )}. Applications that reached production need platform-team decommissioning.`,
      );
    }
    const input = await this.getApplicationBootstrapInput(repoFullName);
    if (!input) {
      throw new Error(
        `${repoFullName} has no application ID: Create stopped before it saved one, so nothing was requested in Azure. Delete the repository in GitHub, under Settings, or ask an organization owner to.`,
      );
    }
    if (!input.appId) {
      throw new Error(
        `${repoFullName} has no application ID: it was created before decommissioning existed. Ask the platform team to remove its resources.`,
      );
    }
    const { vendingRepo, inputs } = await this.decommissionWorkflowInputs();
    if (
      !inputs?.delete_azure ||
      !inputs?.expected_plan_hash ||
      !inputs?.request_id
    ) {
      throw new Error(
        'The Identity Vending Machine needs the reviewed-plan deletion workflow update. Ask the platform team to deploy it before continuing.',
      );
    }
    if (options.environments && !inputs.environments) {
      throw new Error(
        'The Identity Vending Machine can only delete all of the Azure resources. Ask the platform team to deploy the deletion-by-environment update.',
      );
    }
    const requestedAt = Date.now();
    const requestId = globalThis.crypto.randomUUID();
    const otherSubscriptions = environmentSubscriptionsInput(input)
      .split(',')
      .filter(Boolean)
      .map(pair => pair.split('=')[1])
      .join(',');

    await this.dispatchWorkflow(this.vendingRepository, DECOMMISSION_WORKFLOW, {
      ref: vendingRepo.default_branch,
      inputs: {
        repository: repoFullName,
        app_id: input.appId,
        nonprod_subscription_id: input.nonprodSubscriptionId,
        // The IVM deletes the app's prod resource group only while it is empty.
        prod_subscription_id: input.environments.includes('prod')
          ? input.prodSubscriptionId
          : '',
        request_id: requestId,
        expected_plan_hash: options.expectedPlanHash ?? '',
        // An IVM without this input refuses it, so only applications that use it send it.
        ...(otherSubscriptions
          ? { environment_subscription_ids: otherSubscriptions }
          : {}),
        delete_azure: String(options.deleteAzure),
        delete_repository: String(options.deleteRepository),
        // Sent only to delete some environments, so an older IVM still runs full deletions.
        ...(options.environments
          ? { environments: options.environments.join(',') }
          : {}),
        dry_run: String(options.dryRun),
      },
    });
    return { requestedAt, requestId };
  }

  /** Whether the IVM's decommission workflow can delete only some environments. */
  async canDecommissionByEnvironment(): Promise<boolean> {
    if (!this.vendingRepository) return false;
    try {
      const { inputs } = await this.decommissionWorkflowInputs();
      return Boolean(inputs?.environments);
    } catch {
      return false;
    }
  }

  private async decommissionWorkflowInputs() {
    const vendingRepository = this.vendingRepository ?? '';
    const vendingRepo = await this.getRepo(vendingRepository);
    const workflow = await this.getFile(
      vendingRepository,
      `.github/workflows/${DECOMMISSION_WORKFLOW}`,
      vendingRepo.default_branch,
    );
    const contract = workflow ? parseYaml(workflow.content) : undefined;
    return {
      vendingRepo,
      inputs: contract?.on?.workflow_dispatch?.inputs as
        | Record<string, unknown>
        | undefined,
    };
  }

  /**
   * Environments other than sandbox, dev and test (prod, ...) the application
   * was deployed to successfully, from its GitHub deployment records. Any of
   * them means it may have served real users or hold data that must be kept,
   * so the portal won't delete it. Empty when it never deployed. Throws when
   * the deployments can't be read, so callers treat the application as live.
   */
  async liveEnvironmentsBlockingDeletion(
    repoFullName: string,
  ): Promise<string[]> {
    const outside: Array<{ id: number; environment: string }> = [];
    let complete = false;
    for (let page = 1; page <= MAX_DEPLOYMENT_PAGES && !complete; page++) {
      // eslint-disable-next-line no-await-in-loop -- pages are read in order until a short one
      const deployments = await this.request<
        Array<{ id: number; environment: string }>
      >(
        `/repos/${repoFullName}/deployments?per_page=${DEPLOYMENTS_PAGE_SIZE}&page=${page}`,
      );
      outside.push(
        ...deployments.filter(
          deployment =>
            !DELETABLE_DEPLOYMENT_ENVIRONMENTS.has(deployment.environment),
        ),
      );
      complete = deployments.length < DEPLOYMENTS_PAGE_SIZE;
    }
    if (!complete) {
      throw new Error(
        `${repoFullName} has more deployments than the portal checks; ask the platform team to delete it.`,
      );
    }
    const live = await Promise.all(
      outside.map(async deployment => {
        for (let page = 1; page <= MAX_DEPLOYMENT_PAGES; page++) {
          // eslint-disable-next-line no-await-in-loop -- status history is paginated in order
          const statuses = await this.request<Array<{ state: string }>>(
            `/repos/${repoFullName}/deployments/${deployment.id}/statuses?per_page=${DEPLOYMENTS_PAGE_SIZE}&page=${page}`,
          );
          if (
            statuses.some(status => LIVE_DEPLOYMENT_STATES.has(status.state))
          ) {
            return deployment.environment;
          }
          if (statuses.length < DEPLOYMENTS_PAGE_SIZE) return undefined;
        }
        throw new Error(
          'The deployment status history is too large to check safely.',
        );
      }),
    );
    return [
      ...new Set(
        live.filter((environment): environment is string =>
          Boolean(environment),
        ),
      ),
    ].sort((a, b) => a.localeCompare(b));
  }

  /**
   * The newest deployment to each environment the application was deployed
   * to, from GitHub's deployments API, sorted by environment name.
   */
  async listEnvironmentDeployments(
    repoFullName: string,
  ): Promise<EnvironmentDeployment[]> {
    const deployments = await this.request<
      Array<{
        id: number;
        sha: string;
        ref: string;
        environment: string;
        created_at: string;
        creator: { login: string } | null;
      }>
    >(`/repos/${repoFullName}/deployments?per_page=${DEPLOYMENTS_PAGE_SIZE}`);
    // GitHub lists deployments newest first, so the first per environment is the latest.
    const latest = new Map<string, (typeof deployments)[number]>();
    for (const deployment of deployments) {
      if (
        !NON_APPLICATION_ENVIRONMENTS.has(deployment.environment) &&
        !latest.has(deployment.environment)
      ) {
        latest.set(deployment.environment, deployment);
      }
    }
    const withStatus = await Promise.all(
      [...latest.values()].map(async deployment => {
        const [status] = await this.request<
          Array<{ state: string; environment_url?: string; log_url?: string }>
        >(
          `/repos/${repoFullName}/deployments/${deployment.id}/statuses?per_page=1`,
        );
        return {
          environment: deployment.environment,
          sha: deployment.sha,
          ref: deployment.ref,
          createdAt: deployment.created_at,
          creator: deployment.creator?.login,
          state: status?.state,
          environmentUrl: status?.environment_url || undefined,
          logUrl: status?.log_url || undefined,
        };
      }),
    );
    return withStatus.sort((a, b) =>
      a.environment.localeCompare(b.environment),
    );
  }

  /**
   * What deploy.yml (or deploy-sandbox.yml) shipped to an environment, newest
   * first: its deploy:app records. GitHub's own records for jobs that name an
   * environment carry the workflow's commit, not the image's, so they're left out.
   */
  async listAppDeployments(
    repoFullName: string,
    environment: string,
    perPage = 10,
  ): Promise<AppDeployment[]> {
    const params = new URLSearchParams({
      environment,
      task: APP_DEPLOYMENT_TASK,
      per_page: String(perPage),
    });
    const records = await this.request<
      Array<{
        id: number;
        sha: string;
        environment: string;
        created_at: string;
        creator: { login: string } | null;
        payload?: unknown;
      }>
    >(`/repos/${repoFullName}/deployments?${params.toString()}`);
    return Promise.all(
      records.map(async record => {
        const statuses = await this.request<
          Array<{
            state: string;
            description?: string | null;
            environment_url?: string;
            log_url?: string;
          }>
        >(
          `/repos/${repoFullName}/deployments/${record.id}/statuses?per_page=20`,
        );
        const payload = (
          typeof record.payload === 'object' && record.payload !== null
            ? record.payload
            : {}
        ) as { actor?: unknown; branch?: unknown; digests?: unknown };
        return {
          id: record.id,
          environment: record.environment,
          sha: record.sha,
          createdAt: record.created_at,
          actor:
            typeof payload.actor === 'string'
              ? payload.actor
              : record.creator?.login,
          branch:
            typeof payload.branch === 'string' ? payload.branch : undefined,
          state: statuses[0]?.state,
          environmentUrl:
            statuses.find(status => status.environment_url)?.environment_url ||
            undefined,
          logUrl: statuses[0]?.log_url || undefined,
          testsPassed: statuses.some(
            status => status.description === TESTS_PASSED_STATUS,
          ),
          digests: stringRecord(payload.digests),
        };
      }),
    );
  }

  /**
   * What the environment's latest platform set-up left out for lack of Azure
   * capacity ("PostgreSQL", "AI Search"), from its platform record; empty when
   * nothing was, or for templates that don't record it.
   */
  async getDeferredServices(
    repoFullName: string,
    environment: string,
  ): Promise<string[]> {
    const params = new URLSearchParams({
      environment,
      task: PLATFORM_DEPLOYMENT_TASK,
      per_page: '1',
    });
    const [latest] = await this.request<Array<{ payload?: unknown }>>(
      `/repos/${repoFullName}/deployments?${params.toString()}`,
    );
    const deferred =
      typeof latest?.payload === 'object' && latest.payload !== null
        ? (latest.payload as { deferred_services?: unknown }).deferred_services
        : undefined;
    return Array.isArray(deferred)
      ? deferred.filter((item): item is string => typeof item === 'string')
      : [];
  }

  /**
   * The Identity Vending Machine's run that assigns the roles a platform
   * set-up's first pass asked for ("Platform RBAC: <repo> (<env>)"), started
   * after `since`; with the repository it runs in, for its live view.
   */
  async findPlatformRbacRun(
    repoFullName: string,
    environment: string,
    since: string,
  ): Promise<{ repo: string; run: WorkflowRunDetail } | undefined> {
    if (!this.vendingRepository) return undefined;
    const title = `Platform RBAC: ${repoFullName} (${environment})`;
    // Runner clocks and GitHub's created_at can differ slightly.
    const after = Date.parse(since) - 30_000;
    const runs = await this.getWorkflowRuns(
      this.vendingRepository,
      PLATFORM_RBAC_WORKFLOW,
      undefined,
      20,
    );
    const run = runs.find(
      item =>
        item.display_title === title &&
        Date.parse(item.created_at ?? '') >= after,
    );
    return run ? { repo: this.vendingRepository, run } : undefined;
  }

  /** When the newest deployment record with this task was made, if any. */
  async getLatestDeploymentTime(
    repoFullName: string,
    environment: string,
    task: string,
  ) {
    const params = new URLSearchParams({ environment, task, per_page: '1' });
    const [record] = await this.request<Array<{ created_at: string }>>(
      `/repos/${repoFullName}/deployments?${params.toString()}`,
    );
    return record?.created_at;
  }

  /** Repository variables by name; one that isn't set (or can't be read) is left out. */
  /**
   * The repository variables that are set; an unset one (404) is left out.
   * Any other failure throws, so the caller can say the read failed instead
   * of acting as if the variable were unset.
   */
  async getRepositoryVariables(repoFullName: string, names: string[]) {
    const entries = await Promise.all(
      names.map(async name => {
        try {
          const variable = await this.request<{ value?: string }>(
            `/repos/${repoFullName}/actions/variables/${encodeURIComponent(
              name,
            )}`,
          );
          return [name, variable.value] as const;
        } catch (error) {
          if (isGitHubNotFound(error)) return [name, undefined] as const;
          throw error;
        }
      }),
    );
    return Object.fromEntries(
      entries.filter((entry): entry is readonly [string, string] =>
        Boolean(entry[1]),
      ),
    ) as Record<string, string>;
  }

  /** How far head is from base, in commits. */
  async compareCommits(
    repoFullName: string,
    base: string,
    head: string,
  ): Promise<CommitComparison> {
    const comparison = await this.request<{
      ahead_by: number;
      behind_by: number;
      commits?: Array<{
        sha: string;
        commit?: {
          message?: string;
          author?: { name?: string; date?: string } | null;
        };
        author?: { login?: string } | null;
      }>;
    }>(
      `/repos/${repoFullName}/compare/${encodeURIComponent(
        base,
      )}...${encodeURIComponent(head)}`,
    );
    return {
      aheadBy: comparison.ahead_by,
      behindBy: comparison.behind_by,
      commits: (comparison.commits ?? []).map(commit => ({
        sha: commit.sha,
        title: commit.commit?.message?.split('\n')[0] ?? '',
        author: commit.author?.login ?? commit.commit?.author?.name,
        date: commit.commit?.author?.date,
      })),
    };
  }

  /** An environment's protection rules and deployment branches. */
  async getEnvironmentProtection(
    repoFullName: string,
    environment: string,
  ): Promise<EnvironmentProtection> {
    const path = `/repos/${repoFullName}/environments/${encodeURIComponent(
      environment,
    )}`;
    const settings = await this.request<{
      protection_rules?: Array<{
        type: string;
        wait_timer?: number;
        prevent_self_review?: boolean;
        reviewers?: Array<{
          type: string;
          reviewer?: { login?: string; slug?: string; name?: string };
        }>;
      }>;
      deployment_branch_policy?: {
        protected_branches: boolean;
        custom_branch_policies: boolean;
      } | null;
    }>(path);
    const rules = settings.protection_rules ?? [];
    const reviewersRule = rules.find(
      rule => rule.type === 'required_reviewers',
    );
    let branches: EnvironmentProtection['branches'] = 'any';
    const policy = settings.deployment_branch_policy;
    if (policy?.custom_branch_policies) {
      const listed = await this.request<{
        branch_policies?: Array<{ name: string }>;
      }>(`${path}/deployment-branch-policies`);
      branches = (listed.branch_policies ?? []).map(item => item.name);
    } else if (policy?.protected_branches) {
      branches = 'protected';
    }
    return {
      reviewers: (reviewersRule?.reviewers ?? []).map(
        ({ reviewer }) =>
          reviewer?.login ?? reviewer?.slug ?? reviewer?.name ?? 'unknown',
      ),
      preventSelfReview: Boolean(reviewersRule?.prevent_self_review),
      waitTimerMinutes:
        rules.find(rule => rule.type === 'wait_timer')?.wait_timer ?? 0,
      branches,
    };
  }

  /** One app's latest log lines, read by the portal backend from Azure. */
  async getContainerLogs(
    repoFullName: string,
    environment: string,
    app: LogApp,
  ): Promise<ContainerLogs> {
    return this.request<ContainerLogs>(
      `/container-logs/${repoFullName}/${encodeURIComponent(
        environment,
      )}/${app}`,
    );
  }

  /** An environment's variables (names and values; secrets are never read). */
  async getEnvironmentVariables(
    repoFullName: string,
    environment: string,
  ): Promise<Record<string, string>> {
    const response = await this.request<{
      variables?: Array<{ name: string; value: string }>;
    }>(
      `/repos/${repoFullName}/environments/${encodeURIComponent(
        environment,
      )}/variables?per_page=30`,
    );
    return Object.fromEntries(
      (response.variables ?? []).map(variable => [
        variable.name,
        variable.value,
      ]),
    );
  }

  async listBranches(repoFullName: string) {
    const branches = await this.request<Array<{ name: string }>>(
      `/repos/${repoFullName}/branches?per_page=100`,
    );
    return branches.map(branch => branch.name);
  }

  async getCurrentUserLogin() {
    const { login } = await this.request<{ login: string }>('/user');
    return login;
  }

  /**
   * Promotes a commit with deploy.yml. The workflow checks it succeeded in the
   * environment before (and, for prod, passed the gate's tests); a promotion to
   * test continues to prod.
   */
  async promoteRelease(
    repoFullName: string,
    ref: string,
    environment: string,
    commit: string,
  ) {
    await this.dispatchWorkflow(repoFullName, DEPLOY_WORKFLOW, {
      ref,
      inputs: { environment, 'image-tag': commit },
    });
  }

  /** Builds a branch and deploys it to sandbox (deploy-sandbox.yml runs on that branch). */
  async deployBranchToSandbox(repoFullName: string, branch: string) {
    await this.dispatchWorkflow(repoFullName, DEPLOY_SANDBOX_WORKFLOW, {
      ref: branch,
    });
  }

  /**
   * Deletes the sandbox platform: template 1 has teardown-sandbox.yml,
   * template 2 a teardown action in deploy-platform.yml.
   */
  async teardownSandbox(repoFullName: string, ref: string) {
    if (await this.hasWorkflow(repoFullName, TEARDOWN_SANDBOX_WORKFLOW)) {
      await this.dispatchWorkflow(repoFullName, TEARDOWN_SANDBOX_WORKFLOW, {
        ref,
      });
      return;
    }
    await this.dispatchWorkflow(repoFullName, DEPLOY_PLATFORM_WORKFLOW, {
      ref,
      inputs: { environment: 'sandbox', action: 'teardown' },
    });
  }

  /**
   * Asks the Identity Vending Machine to apply the environment rules: dev,
   * test and prod deploy from main only (prod from v* release tags with the
   * SDLC kit), prod has reviewers, and main needs a
   * reviewed pull request once setup is applied. The IVM only tightens them.
   */
  async applyEnvironmentRules(repoFullName: string) {
    if (!this.vendingRepository) {
      throw new Error(
        'The portal has no Identity Vending Machine repository configured',
      );
    }
    const vendingRepo = await this.getRepo(this.vendingRepository);
    await this.dispatchWorkflow(
      this.vendingRepository,
      ENVIRONMENT_RULES_WORKFLOW,
      {
        ref: vendingRepo.default_branch,
        inputs: { requesting_repo: repoFullName },
      },
    );
  }

  /** False once the repository is gone (deleted or decommissioned); other failures throw. */
  async repositoryExists(repoFullName: string) {
    try {
      await this.getRepo(repoFullName);
      return true;
    } catch (error) {
      if (isGitHubNotFound(error)) return false;
      throw error;
    }
  }

  /**
   * True once any run of the application's deploy workflow has succeeded, even
   * if a later one failed.
   */
  async hasSuccessfulDeployment(repoFullName: string) {
    try {
      const response = await this.request<{ total_count?: number }>(
        `/repos/${repoFullName}/actions/workflows/${DEPLOY_WORKFLOW}/runs?status=success&per_page=1`,
      );
      return (response.total_count ?? 0) > 0;
    } catch (error) {
      // No deploy workflow (yet) means nothing was ever deployed.
      if (isGitHubNotFound(error)) return false;
      throw error;
    }
  }

  /**
   * The decommission run started for this repository at or after
   * `requestedAt`. GitHub returns no run ID for a dispatch, so it is found by
   * its exact repository/request-ID title. Undefined until it appears.
   */
  async findDecommissionRun(
    repoFullName: string,
    requestedAt: number,
    requestId: string,
  ) {
    if (!this.vendingRepository) return undefined;
    const titles = new Set([
      `Decommission: ${repoFullName} [${requestId}]`,
      `Decommission: ${repoFullName} [${requestId}] (dry run)`,
    ]);
    // Runner clocks and GitHub's created_at can differ slightly.
    const since = requestedAt - 30_000;
    const runs = await this.getWorkflowRuns(
      this.vendingRepository,
      DECOMMISSION_WORKFLOW,
    );
    return runs.find(
      run =>
        titles.has(run.display_title ?? '') &&
        Date.parse(run.created_at ?? '') >= since,
    );
  }

  /**
   * The application's unfinished deletion run, so its progress can be shown
   * after the dialog that started it was closed. Plan-only runs end their
   * title with "(dry run)" and are ignored.
   */
  async findActiveDecommission(
    repoFullName: string,
  ): Promise<ActiveDecommission | undefined> {
    return (await this.listActiveDecommissions()).get(repoFullName);
  }

  /** Every application's unfinished deletion run, by repository full name. */
  async listActiveDecommissions(): Promise<Map<string, ActiveDecommission>> {
    const active = new Map<string, ActiveDecommission>();
    if (!this.vendingRepository) return active;
    const prefix = 'Decommission: ';
    const runs = await this.getWorkflowRuns(
      this.vendingRepository,
      DECOMMISSION_WORKFLOW,
      undefined,
      ACTIVE_DECOMMISSION_RUN_SCAN,
    );
    for (const run of runs) {
      const title = run.display_title ?? '';
      const bracket = title.lastIndexOf(' [');
      if (
        run.status === 'completed' ||
        !run.id ||
        !title.startsWith(prefix) ||
        !title.endsWith(']') ||
        bracket < prefix.length
      ) {
        continue;
      }
      const repository = title.slice(prefix.length, bracket);
      // Runs come newest first: keep the latest per application.
      if (active.has(repository)) continue;
      active.set(repository, {
        runId: run.id,
        requestId: title.slice(bracket + 2, -1),
        requestedAt: Date.parse(run.created_at ?? ''),
        status: run.status,
        htmlUrl: run.html_url,
      });
    }
    return active;
  }

  /**
   * The IVM's live resource group deletion progress, once its delete job has
   * started. Best effort: without it the dialog shows the job's steps only.
   */
  private async getResourceGroupDeletion(
    repo: string,
    run: WorkflowRun,
    runId: number,
    jobs: DecommissionJob[],
  ): Promise<ResourceGroupDeletionProgress | undefined> {
    const deleteJob = jobs.find(job => job.name === DECOMMISSION_DELETE_JOB);
    if (
      !run.head_sha ||
      !deleteJob?.startedAt ||
      deleteJob.conclusion === 'skipped'
    ) {
      return undefined;
    }
    const checkName = `${DECOMMISSION_PROGRESS_CHECK}/${runId}`;
    try {
      const response = await this.request<CheckRunsResponse>(
        `/repos/${repo}/commits/${
          run.head_sha
        }/check-runs?check_name=${encodeURIComponent(checkName)}&filter=latest`,
      );
      const checkRun = response.check_runs?.find(
        candidate => candidate.external_id === String(runId),
      );
      return parseResourceGroupDeletion(checkRun?.output?.text);
    } catch {
      return undefined;
    }
  }

  /**
   * Jobs and steps of the Identity Vending Machine run that provisions an
   * application, plus live Bicep progress of the Provision jobs that are
   * deploying, when the IVM publishes it.
   */
  async getProvisioningProgress(runId: number): Promise<ProvisioningProgress> {
    const repo = this.vendingRepository;
    if (!repo) {
      throw new Error(
        'The Identity Vending Machine repository is not configured.',
      );
    }
    const [run, jobsResponse] = await Promise.all([
      this.request<WorkflowRun>(`/repos/${repo}/actions/runs/${runId}`),
      this.request<WorkflowRunJobsResponse>(
        `/repos/${repo}/actions/runs/${runId}/jobs?per_page=100`,
      ),
    ]);
    const jobs = toRunJobs(jobsResponse);
    const deploying = jobs
      .filter(job => job.status === 'in_progress')
      .map(job => PROVISION_JOB_PATTERN.exec(job.name)?.[1])
      .filter((label): label is string => Boolean(label));
    const deployments: Record<string, DeploymentProgress> = {};
    if (run.head_sha) {
      const found = await Promise.all(
        deploying.map(label =>
          this.getDeploymentProgress(repo, run.head_sha!, runId, label),
        ),
      );
      for (const deployment of found) {
        if (deployment) deployments[deployment.label] = deployment;
      }
    }
    return { run, jobs, deployments };
  }

  /**
   * The agent's steps in a dispatcher run, as invoke-agent.yml reports them.
   * Undefined when the run has no activity check run (its caller doesn't
   * grant `checks: write`) or it can't be read; the run's steps still show.
   */
  async getAgentActivity(
    repoFullName: string,
    run: Pick<WorkflowRunDetail, 'id' | 'head_sha'>,
  ): Promise<AgentActivity | undefined> {
    try {
      const response = await this.request<CheckRunsResponse>(
        `/repos/${repoFullName}/commits/${
          run.head_sha
        }/check-runs?check_name=${encodeURIComponent(AGENT_ACTIVITY_CHECK)}`,
      );
      const checkRun = response.check_runs?.find(
        candidate => candidate.external_id === String(run.id),
      );
      if (!checkRun) return undefined;
      return {
        summary: checkRun.output?.summary ?? undefined,
        completed: checkRun.status === 'completed',
        events: parseAgentActivityText(checkRun.output?.text),
      };
    } catch {
      return undefined;
    }
  }

  /** Best effort: without it the timeline shows the job's steps only. */
  private async getDeploymentProgress(
    repo: string,
    headSha: string,
    runId: number,
    label: string,
  ): Promise<DeploymentProgress | undefined> {
    const checkName = `${IDENTITY_PROGRESS_CHECK}/${runId}/${label}`;
    try {
      const response = await this.request<CheckRunsResponse>(
        `/repos/${repo}/commits/${headSha}/check-runs?check_name=${encodeURIComponent(
          checkName,
        )}&filter=latest`,
      );
      const checkRun = response.check_runs?.find(
        candidate => candidate.external_id === String(runId),
      );
      return parseDeploymentProgress(checkRun?.output?.text);
    } catch {
      return undefined;
    }
  }

  /** Job states of a decommission run, plus its plan once the plan job is done. */
  async getDecommissionProgress(
    runId: number,
    options: { includePlan: boolean },
  ): Promise<DecommissionProgress> {
    const repo = this.vendingRepository;
    if (!repo) {
      throw new Error(
        'The Identity Vending Machine repository is not configured.',
      );
    }
    const [run, jobsResponse] = await Promise.all([
      this.request<WorkflowRun>(`/repos/${repo}/actions/runs/${runId}`),
      this.request<WorkflowRunJobsResponse>(
        `/repos/${repo}/actions/runs/${runId}/jobs?per_page=100`,
      ),
    ]);
    const jobs = toRunJobs(jobsResponse);
    const progress: DecommissionProgress = {
      run,
      jobs,
      deletion: await this.getResourceGroupDeletion(repo, run, runId, jobs),
    };

    const planJob = (jobsResponse.jobs ?? []).find(
      job => job.name === DECOMMISSION_PLAN_JOB,
    );
    const checkRunId = parseCheckRunId(planJob?.check_run_url);
    if (
      !options.includePlan ||
      planJob?.status !== 'completed' ||
      !checkRunId
    ) {
      return progress;
    }

    let annotations: CheckRunAnnotation[];
    try {
      annotations = await this.request<CheckRunAnnotation[]>(
        `/repos/${repo}/check-runs/${checkRunId}/annotations`,
      );
    } catch {
      // Job states still come through; only the plan details are missing.
      progress.planUnavailable = true;
      return progress;
    }
    const planAnnotation = annotations.find(
      annotation => annotation.title === 'decommission-plan',
    );
    if (planAnnotation?.message) {
      try {
        progress.plan = JSON.parse(planAnnotation.message) as DecommissionPlan;
      } catch {
        // A malformed plan leaves the run link as the only feedback.
      }
    }
    progress.refusal = annotations.find(
      annotation => annotation.title === 'decommission-refused',
    )?.message;
    return progress;
  }

  async getApplicationBootstrapInput(repoFullName: string) {
    const repo = await this.getRepo(repoFullName);
    const file = await this.getFile(
      repoFullName,
      PORTAL_BOOTSTRAP_INPUT_PATH,
      repo.default_branch,
    );

    if (!file) {
      return undefined;
    }

    return JSON.parse(file.content) as ApplicationBootstrapInput;
  }

  async syncApplicationBootstrapInput(
    repoFullName: string,
    formInput: ApplicationBootstrapInput,
  ) {
    const repo = await this.getRepo(repoFullName);
    // The form doesn't carry the app ID; keep the one assigned at creation.
    const existing = await this.getApplicationBootstrapInput(repoFullName);
    const input = { ...formInput, appId: formInput.appId ?? existing?.appId };

    await this.upsertFile({
      repoFullName,
      branch: repo.default_branch,
      path: PORTAL_BOOTSTRAP_INPUT_PATH,
      content: buildBootstrapInputJson(input),
      message: 'chore(portal): refresh bootstrap input [ai-assisted]',
    });

    await this.upsertFile({
      repoFullName,
      branch: repo.default_branch,
      path: REQUIREMENTS_TEMPLATE_PATH,
      content: buildRequirementsTemplate(input),
      message:
        'docs(requirements): refresh personalized template [ai-assisted]',
    });
  }

  async createRequirementsPullRequest(repoFullName: string, content: string) {
    const repo = await this.getRepo(repoFullName);

    // Check for existing open requirements PRs to avoid duplicates
    const openPrs = await this.request<
      Array<{ html_url: string; head: { ref: string } }>
    >(`/repos/${repoFullName}/pulls?state=open&per_page=30`);
    const existingPr = openPrs.find(pr =>
      pr.head.ref.startsWith(REQUIREMENTS_BRANCH_PREFIX),
    );
    if (existingPr) {
      return existingPr.html_url;
    }

    const branchName = `${REQUIREMENTS_BRANCH_PREFIX}${Date.now()}`;
    await this.createBranch(repoFullName, repo.default_branch, branchName);

    await this.upsertFile({
      repoFullName,
      branch: branchName,
      path: REQUIREMENTS_JSON_PATH,
      content,
      message: 'docs(requirements): add structured requirements [ai-assisted]',
    });

    const pullRequest = await this.request<{ html_url: string }>(
      `/repos/${repoFullName}/pulls`,
      {
        method: 'POST',
        body: JSON.stringify({
          title: 'docs(requirements): add business requirements (JSON)',
          head: branchName,
          base: repo.default_branch,
          body: [
            '## Summary',
            '',
            '- Structured business requirements in `docs/requirements.json`',
            '- Generated via the Vivicta Developer Portal requirements wizard',
            '',
            'Merging it starts the requirement agent, which opens a pull request with the specification, architecture, compliance and test-plan documents.',
          ].join('\n'),
        }),
      },
    );

    return pullRequest.html_url;
  }

  /**
   * Opens a pull request that replaces setup-config.yaml with `content`, on a
   * new branch off the default branch. Once merged, setup runs again and
   * opens its own pull request with the files the new values fill in.
   */
  async createSetupConfigPullRequest(
    repoFullName: string,
    content: string,
    fields: readonly string[],
  ) {
    const repo = await this.getRepo(repoFullName);
    const branchName = `${SETUP_SETTINGS_BRANCH_PREFIX}${Date.now()}`;
    await this.createBranch(repoFullName, repo.default_branch, branchName);

    await this.upsertFile({
      repoFullName,
      branch: branchName,
      path: SETUP_CONFIG_PATH,
      content,
      message: 'chore(setup): fill in remaining settings [ai-assisted]',
    });

    const pullRequest = await this.request<{ html_url: string }>(
      `/repos/${repoFullName}/pulls`,
      {
        method: 'POST',
        body: JSON.stringify({
          title: 'chore(setup): fill in remaining settings',
          head: branchName,
          base: repo.default_branch,
          body: [
            '## Summary',
            '',
            `Sets ${
              fields.length === 1 ? 'a setting' : 'settings'
            } in \`${SETUP_CONFIG_PATH}\` that setup left as placeholders:`,
            '',
            ...fields.map(field => `- \`${field}\``),
            '',
            'Filled in from the Vivicta Developer Portal. After this merges, setup runs again and opens a pull request that applies the values.',
          ].join('\n'),
        }),
      },
    );

    return pullRequest.html_url;
  }

  private async createBranch(
    repoFullName: string,
    baseBranch: string,
    branchName: string,
  ) {
    const ref = await this.request<{ object: { sha: string } }>(
      `/repos/${repoFullName}/git/ref/heads/${baseBranch}`,
    );
    await this.request(`/repos/${repoFullName}/git/refs`, {
      method: 'POST',
      body: JSON.stringify({
        ref: `refs/heads/${branchName}`,
        sha: ref.object.sha,
      }),
    });
  }

  // ── User-token request (for PO-attributed operations) ──────────────

  private async userRequest<T>(
    path: string,
    init?: RequestInit & { expectText?: boolean },
  ) {
    if (!this.userTokenProvider) {
      throw new Error(
        'User token provider is not configured — sign in with GitHub first',
      );
    }

    const token = await this.userTokenProvider();
    const response = await this.fetchApi.fetch(
      `${this.applicationPortalBaseUrl}/user-actions${path}`,
      {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          'X-GitHub-Token': token,
          ...init?.headers,
        },
      },
    );

    if (!response.ok) {
      const errorBody = await response.text();
      throw githubApiError(
        response.status,
        errorBody,
        'GitHub user request failed',
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    if (init?.expectText) {
      return (await response.text()) as T;
    }

    return (await response.json()) as T;
  }

  // ── PR lifecycle (read via App token) ──────────────────────────────

  async listPullRequests(
    repoFullName: string,
    state: 'open' | 'closed' | 'all' = 'open',
  ) {
    return this.request<PullRequest[]>(
      `/repos/${repoFullName}/pulls?state=${state}&per_page=30&sort=updated&direction=desc`,
    );
  }

  async getPullRequest(repoFullName: string, pullNumber: number) {
    return this.request<PullRequestDetail>(
      `/repos/${repoFullName}/pulls/${pullNumber}`,
    );
  }

  /** How many of a commit's latest check runs are still going, and how many failed. */
  async getCheckRunCounts(
    repoFullName: string,
    sha: string,
  ): Promise<CheckRunCounts> {
    const response = await this.request<{
      check_runs?: Array<{ status?: string; conclusion?: string | null }>;
    }>(
      `/repos/${repoFullName}/commits/${sha}/check-runs?filter=latest&per_page=100`,
    );
    const runs = response.check_runs ?? [];
    return {
      running: runs.filter(run => run.status !== 'completed').length,
      failed: runs.filter(run =>
        FAILED_CHECK_CONCLUSIONS.has(run.conclusion ?? ''),
      ).length,
    };
  }

  async getPullRequestFiles(repoFullName: string, pullNumber: number) {
    return this.request<PullRequestFile[]>(
      `/repos/${repoFullName}/pulls/${pullNumber}/files`,
    );
  }

  async getPullRequestComments(repoFullName: string, pullNumber: number) {
    return this.request<PullRequestComment[]>(
      `/repos/${repoFullName}/issues/${pullNumber}/comments`,
    );
  }

  async getFileContent(repoFullName: string, path: string, ref?: string) {
    return this.getFile(repoFullName, path, ref);
  }

  async getWorkflowRuns(
    repoFullName: string,
    workflowFile: string,
    branch?: string,
    perPage = 5,
  ) {
    const params = new URLSearchParams({ per_page: String(perPage) });
    if (branch) {
      params.set('branch', branch);
    }

    const response = await this.request<{ workflow_runs: WorkflowRunDetail[] }>(
      `/repos/${repoFullName}/actions/workflows/${encodeURIComponent(
        workflowFile,
      )}/runs?${params.toString()}`,
    );

    return response.workflow_runs ?? [];
  }

  /** Every workflow run in the repository started at or after `since` (ISO time), newest first. */
  async getWorkflowRunsSince(repoFullName: string, since: string) {
    // Filtered here: `created=>=…` is blocked by the gateway's firewall (SQL operator rule)
    const response = await this.request<{ workflow_runs: WorkflowRunDetail[] }>(
      `/repos/${repoFullName}/actions/runs?per_page=50`,
    );
    const from = Date.parse(since);
    return (response.workflow_runs ?? []).filter(
      run => Date.parse(run.created_at) >= from,
    );
  }

  /**
   * Registers the repository's catalog-info.yaml with the catalog. The portal's
   * backend does it, so no URL travels in the browser's request.
   */
  async registerInCatalog(repoFullName: string): Promise<void> {
    await this.request<void>('/catalog-locations', {
      method: 'POST',
      body: JSON.stringify({ repository: repoFullName }),
    });
  }

  /**
   * Registers applications whose setup PR is merged, once per session. Until
   * then catalog-info.yaml holds the template's placeholders, which the
   * catalog rejects on every refresh.
   */
  private registerSetUpApplications(applications: ApplicationSummary[]) {
    const unregistered = applications.filter(
      app =>
        app.setupMergedRun?.conclusion === 'success' &&
        !this.catalogRegistrations.has(app.fullName),
    );
    for (const app of unregistered) {
      this.catalogRegistrations.add(app.fullName);
      // Already registered answers 409, which the backend accepts
      this.registerInCatalog(app.fullName).catch(() => undefined);
    }
  }

  /** Removes a deleted application's catalog location and entities, through the portal's backend. */
  async removeFromCatalog(repoFullName: string): Promise<void> {
    await this.request<void>(`/catalog-locations/${repoFullName}`, {
      method: 'DELETE',
    });
  }

  /** Whether the repository has this workflow file under .github/workflows. */
  async hasWorkflow(repoFullName: string, workflowFile: string) {
    return (await this.getAvailableWorkflowFiles(repoFullName)).has(
      workflowFile,
    );
  }

  /** Starts the template's Contributor-only platform deploy (first pass). */
  /**
   * Starts an environment's platform set-up. With dataServicesLocation, a
   * person chose another allowed region for PostgreSQL and AI Search (used
   * only while they don't exist yet).
   */
  async startPlatformDeploy(
    repoFullName: string,
    ref: string,
    environment: string,
    dataServicesLocation?: string,
  ) {
    await this.dispatchWorkflow(repoFullName, DEPLOY_PLATFORM_WORKFLOW, {
      ref,
      inputs: {
        environment,
        ...(dataServicesLocation
          ? { data_services_location: dataServicesLocation }
          : {}),
      },
    });
  }

  /**
   * The regions an environment's PostgreSQL and AI Search may be placed in,
   * first one first: the organization variable the templates read, else
   * their default.
   */
  async getAllowedDataRegions(repoFullName: string): Promise<string[]> {
    try {
      const response = await this.request<{
        variables?: Array<{ name: string; value: string }>;
      }>(`/repos/${repoFullName}/actions/organization-variables?per_page=30`);
      const value = response.variables?.find(
        variable => variable.name === ALLOWED_DATA_REGIONS_VARIABLE,
      )?.value;
      const regions = (value ?? '')
        .split(',')
        .map(region => region.trim())
        .filter(Boolean);
      return regions.length > 0 ? regions : DEFAULT_DATA_REGIONS;
    } catch {
      return DEFAULT_DATA_REGIONS;
    }
  }

  /** A run in the application's repository with its jobs and their steps, for a live view. */
  async getWorkflowRunProgress(
    repoFullName: string,
    runId: number,
  ): Promise<{ run: WorkflowRun; jobs: DecommissionJob[] }> {
    const [run, jobsResponse] = await Promise.all([
      this.request<WorkflowRun>(`/repos/${repoFullName}/actions/runs/${runId}`),
      this.request<WorkflowRunJobsResponse>(
        `/repos/${repoFullName}/actions/runs/${runId}/jobs?per_page=100`,
      ),
    ]);
    return { run, jobs: toRunJobs(jobsResponse) };
  }

  /** Environments a run is waiting on for a reviewer's approval. */
  async getPendingDeploymentEnvironments(repoFullName: string, runId: number) {
    const pending = await this.request<
      Array<{ environment?: { name?: string } }>
    >(`/repos/${repoFullName}/actions/runs/${runId}/pending_deployments`);
    return pending
      .map(item => item.environment?.name)
      .filter((name): name is string => Boolean(name));
  }

  /** Names of the run's jobs that failed. */
  async getFailedWorkflowJobs(repoFullName: string, runId: number) {
    const response = await this.request<WorkflowRunJobsResponse>(
      `/repos/${repoFullName}/actions/runs/${runId}/jobs?per_page=100`,
    );
    return (response.jobs ?? [])
      .filter(job => job.conclusion === 'failure')
      .map(job => job.name);
  }

  /** Runs a finished workflow run again, all jobs, with the repository's current variables. */
  /**
   * Stops an application's provisioning so what it created can be deleted
   * without racing it: cancels the latest request-identity run and the IVM
   * run that serves it, and keeps looking for the IVM run (it can start after
   * the request was sent) until both have ended or `timeoutMs` passes.
   */
  async cancelProvisioning(
    repoFullName: string,
    { timeoutMs = 90_000, pollMs = 3_000 } = {},
  ) {
    const cancelled = new Set<number>();
    const cancel = async (repo: string, run?: WorkflowRun) => {
      if (!run?.id || run.status === 'completed' || cancelled.has(run.id)) {
        return;
      }
      cancelled.add(run.id);
      await this.request(`/repos/${repo}/actions/runs/${run.id}/cancel`, {
        method: 'POST',
      }).catch(() => undefined);
    };
    const deadline = Date.now() + timeoutMs;
    let quietRounds = 0;
    for (;;) {
      // eslint-disable-next-line no-await-in-loop -- each round re-reads the runs
      const requestRun = await this.getLatestWorkflowRun(
        repoFullName,
        REQUEST_IDENTITY_WORKFLOW,
      );
      const vendingRun =
        requestRun && this.vendingRepository
          ? // eslint-disable-next-line no-await-in-loop -- needs the request run
            await this.findVendingRun(repoFullName, requestRun)
          : undefined;
      // eslint-disable-next-line no-await-in-loop -- cancel before checking again
      await Promise.all([
        cancel(repoFullName, requestRun),
        this.vendingRepository
          ? cancel(this.vendingRepository, vendingRun)
          : Promise.resolve(),
      ]);
      const requestEnded = !requestRun || requestRun.status === 'completed';
      // A request that dispatched successfully has an IVM run coming; one
      // cancelled mid-way may still have dispatched, so look a little longer.
      if (requestEnded && !vendingRun) quietRounds += 1;
      const ended =
        requestEnded &&
        (!this.vendingRepository ||
          vendingRun?.status === 'completed' ||
          (!vendingRun &&
            requestRun?.conclusion !== 'success' &&
            quietRounds > QUIET_ROUNDS_WITHOUT_IVM_RUN));
      if (ended || Date.now() >= deadline) return ended;
      // eslint-disable-next-line no-await-in-loop -- intentional polling delay between checks
      await delay(pollMs);
    }
  }

  async rerunWorkflowRun(repoFullName: string, runId: number) {
    await this.request(`/repos/${repoFullName}/actions/runs/${runId}/rerun`, {
      method: 'POST',
    });
  }

  /** The application's latest identity request, and whether it or the IVM run answering it is still going. */
  async getIdentityRequestStatus(
    repoFullName: string,
    now = Date.now(),
  ): Promise<IdentityRequestStatus> {
    const requestRun = await this.getLatestWorkflowRun(
      repoFullName,
      REQUEST_IDENTITY_WORKFLOW,
    );
    if (!requestRun?.id) {
      return { inProgress: false, rerunnable: false };
    }
    const vendingRun =
      requestRun.status === 'completed'
        ? await this.findVendingRun(repoFullName, requestRun)
        : undefined;
    return {
      requestRun,
      inProgress:
        requestRun.status !== 'completed' ||
        (vendingRun !== undefined && vendingRun.status !== 'completed'),
      rerunnable:
        now - Date.parse(requestRun.created_at ?? '') < RERUN_WINDOW_MS,
    };
  }

  /**
   * Has the IVM handle the application's identity request again, which adds
   * any resource or role assignment it lacks. Re-runs the latest
   * request-identity.yml run with its original inputs; GitHub only re-runs a
   * run for 30 days, so an older one is requested again from the setup input
   * saved at creation, which needs Owner on the subscriptions like the first
   * request did. Returns the re-run's page, unknown for a new request.
   */
  async rerunIdentityRequest(
    repoFullName: string,
  ): Promise<{ htmlUrl?: string }> {
    const status = await this.getIdentityRequestStatus(repoFullName);
    if (status.inProgress) {
      throw new Error(
        'An identity request is already running for this application. Wait for it to finish.',
      );
    }
    if (status.requestRun?.id && status.rerunnable) {
      await this.rerunWorkflowRun(repoFullName, status.requestRun.id);
      return { htmlUrl: status.requestRun.html_url };
    }
    await this.resumeIdentityProvisioning(repoFullName);
    return {};
  }

  /** Re-runs only the failed jobs (and what depends on them), keeping the passed ones. */
  async rerunFailedJobs(repoFullName: string, runId: number) {
    await this.request(
      `/repos/${repoFullName}/actions/runs/${runId}/rerun-failed-jobs`,
      { method: 'POST' },
    );
  }

  /** Names of the run's jobs that were skipped (e.g. a gate job decided not to run them). */
  async getSkippedWorkflowJobs(repoFullName: string, runId: number) {
    const response = await this.request<WorkflowRunJobsResponse>(
      `/repos/${repoFullName}/actions/runs/${runId}/jobs?per_page=100`,
    );
    return (response.jobs ?? [])
      .filter(job => job.conclusion === 'skipped')
      .map(job => job.name);
  }

  async getWorkflowRunFailureReason(repoFullName: string, runId: number) {
    return describeWorkflowRunFailure(
      await this.getWorkflowRunFailure(repoFullName, runId),
    );
  }

  /** Where a run failed (first failed job and step) plus any check-run output. */
  async getWorkflowRunFailure(
    repoFullName: string,
    runId: number,
  ): Promise<WorkflowRunFailure | undefined> {
    const response = await this.request<WorkflowRunJobsResponse>(
      `/repos/${repoFullName}/actions/runs/${runId}/jobs?per_page=100`,
    );

    const failedJobs = (response.jobs ?? []).filter(job =>
      FAILED_STEP_CONCLUSIONS.has(job.conclusion ?? ''),
    );
    const [firstFailedJob] = failedJobs;
    if (!firstFailedJob) {
      return undefined;
    }

    let details: string | undefined;
    for (const job of failedJobs) {
      // eslint-disable-next-line no-await-in-loop -- stop at the first job that has check-run output
      details = await this.getCheckRunDetails(repoFullName, job.check_run_url);
      if (details) {
        break;
      }
    }

    return {
      job: firstFailedJob.name,
      step: (firstFailedJob.steps ?? []).find(step =>
        FAILED_STEP_CONCLUSIONS.has(step.conclusion ?? ''),
      )?.name,
      details,
    };
  }

  private async getCheckRunDetails(repoFullName: string, checkRunUrl?: string) {
    const checkRunId = parseCheckRunId(checkRunUrl);
    if (!checkRunId) {
      return undefined;
    }

    try {
      const checkRun = await this.request<CheckRunResponse>(
        `/repos/${repoFullName}/check-runs/${checkRunId}`,
        { headers: { Accept: 'application/vnd.github+json' } },
      );
      const output = [
        checkRun.output?.summary,
        checkRun.output?.text,
        checkRun.output?.title,
      ]
        .filter((value): value is string => Boolean(value))
        .map(normalizeFailureMessage)
        .find(Boolean);
      return (
        output ?? (await this.getFailureAnnotation(repoFullName, checkRunId))
      );
    } catch {
      return undefined;
    }
  }

  /**
   * Actions writes a failed step's error as an annotation, not as check-run
   * output. Every failure annotation counts, one per line, with its title: the
   * templates name the service and Azure's error code there.
   */
  private async getFailureAnnotation(repoFullName: string, checkRunId: number) {
    const annotations = await this.request<CheckRunAnnotation[]>(
      `/repos/${repoFullName}/check-runs/${checkRunId}/annotations?per_page=50`,
    );
    const messages = annotations
      .filter(annotation => annotation.annotation_level === 'failure')
      .map(annotation => {
        const message = normalizeFailureMessage(annotation.message ?? '');
        const title = normalizeFailureMessage(annotation.title ?? '');
        return title && message && !message.startsWith(title)
          ? `${title}: ${message}`
          : message || title;
      })
      .filter(message => message && !GENERIC_EXIT_ANNOTATION.test(message));
    return messages.length > 0 ? messages.join('\n') : undefined;
  }

  // ── PO-attributed write operations (user token) ───────────────────

  async postPullRequestComment(
    repoFullName: string,
    pullNumber: number,
    body: string,
  ) {
    return this.userRequest<PullRequestComment>(
      `/repos/${repoFullName}/issues/${pullNumber}/comments`,
      {
        method: 'POST',
        body: JSON.stringify({ body }),
      },
    );
  }

  /**
   * A comment on a whole file of the pull request (not a line). The
   * requirement agent reads these as feedback for that document on `/revise`.
   */
  async postPullRequestFileComment(options: {
    repoFullName: string;
    pullNumber: number;
    commitId: string;
    path: string;
    body: string;
  }) {
    return this.userRequest<PullRequestComment>(
      `/repos/${options.repoFullName}/pulls/${options.pullNumber}/comments`,
      {
        method: 'POST',
        body: JSON.stringify({
          body: options.body,
          commit_id: options.commitId,
          path: options.path,
          subject_type: 'file',
        }),
      },
    );
  }

  async updatePullRequestFile(options: {
    repoFullName: string;
    path: string;
    branch: string;
    content: string;
    message?: string;
  }) {
    const existing = await this.getFile(
      options.repoFullName,
      options.path,
      options.branch,
    );

    return this.userRequest(
      `/repos/${options.repoFullName}/contents/${options.path}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          message:
            options.message ??
            `docs(review): update ${getFileName(
              options.path,
            )} from portal review [ai-assisted]`,
          content: encodeBase64Utf8(options.content),
          branch: options.branch,
          sha: existing?.sha,
        }),
      },
    );
  }

  /**
   * Approves, then merges. Main's protection wants an approval from someone
   * other than the last pusher; the App pushes the portal's branches, so the
   * user's approval counts.
   */
  async mergePullRequest(
    repoFullName: string,
    pullNumber: number,
    commitTitle?: string,
  ) {
    await this.userRequest(
      `/repos/${repoFullName}/pulls/${pullNumber}/reviews`,
      {
        method: 'POST',
        body: JSON.stringify({ event: 'APPROVE' }),
      },
    );
    return this.userRequest<MergeResult>(
      `/repos/${repoFullName}/pulls/${pullNumber}/merge`,
      {
        method: 'PUT',
        body: JSON.stringify({
          merge_method: 'squash',
          ...(commitTitle ? { commit_title: commitTitle } : {}),
        }),
      },
    );
  }

  // ── Checkpoint reviews (gate-owner PR reviews, phase 1) ───────────

  /** Every review on the pull request, oldest first (up to 100). */
  async listPullRequestReviews(repoFullName: string, pullNumber: number) {
    return this.request<PullRequestReview[]>(
      `/repos/${repoFullName}/pulls/${pullNumber}/reviews?per_page=100`,
    );
  }

  /** The pull request's commits, for its authors and committers of record. */
  async listPullRequestCommits(repoFullName: string, pullNumber: number) {
    return this.request<PullRequestCommit[]>(
      `/repos/${repoFullName}/pulls/${pullNumber}/commits?per_page=100`,
    );
  }

  /**
   * A review as the signed-in person, on the exact commit they read. Line
   * comments carry `suggestion` blocks ("Suggest change").
   */
  async submitReview(options: {
    repoFullName: string;
    pullNumber: number;
    commitId: string;
    event: ReviewEvent;
    body: string;
    comments?: ReviewLineComment[];
  }) {
    return this.userRequest<PullRequestReview>(
      `/repos/${options.repoFullName}/pulls/${options.pullNumber}/reviews`,
      {
        method: 'POST',
        body: JSON.stringify({
          commit_id: options.commitId,
          event: options.event,
          body: options.body,
          ...(options.comments?.length && { comments: options.comments }),
        }),
      },
    );
  }

  async requestReviewers(
    repoFullName: string,
    pullNumber: number,
    reviewers: string[],
  ) {
    return this.userRequest<PullRequestDetail>(
      `/repos/${repoFullName}/pulls/${pullNumber}/requested_reviewers`,
      { method: 'POST', body: JSON.stringify({ reviewers }) },
    );
  }

  async removeRequestedReviewer(
    repoFullName: string,
    pullNumber: number,
    reviewers: string[],
  ) {
    return this.userRequest<PullRequestDetail>(
      `/repos/${repoFullName}/pulls/${pullNumber}/requested_reviewers`,
      { method: 'DELETE', body: JSON.stringify({ reviewers }) },
    );
  }

  /**
   * Turns on squash auto-merge as the signed-in person, so GitHub merges once
   * the gate passes. Throws when the repository doesn't allow auto-merge.
   */
  async enableAutoMerge(pullRequestNodeId: string, owner: string) {
    const response = await this.userRequest<{
      data?: unknown;
      errors?: Array<{ message?: string }>;
    }>('/graphql', {
      method: 'POST',
      body: JSON.stringify({
        query: ENABLE_AUTO_MERGE_MUTATION,
        variables: {
          pullRequestId: pullRequestNodeId,
          mergeMethod: 'SQUASH',
          owner,
        },
      }),
    });
    if (response?.errors?.length) {
      throw new Error(
        `Auto-merge couldn't be turned on: ${response.errors
          .map(error => error.message ?? 'unknown error')
          .join('; ')}`,
      );
    }
  }

  /**
   * The fallback when auto-merge is unavailable and every required approval
   * is in: squash-merges only if the head is still the commit decided on.
   */
  async mergeApprovedPullRequest(
    repoFullName: string,
    pullNumber: number,
    headSha: string,
  ) {
    return this.userRequest<MergeResult>(
      `/repos/${repoFullName}/pulls/${pullNumber}/merge`,
      {
        method: 'PUT',
        body: JSON.stringify({ merge_method: 'squash', sha: headSha }),
      },
    );
  }

  // ── Template Discovery ───────────────────────────────────────────

  /**
   * Discover all template repos in the org by GitHub topic and fetch
   * their manifests. Results are cached for 5 minutes.
   */
  async listTemplates(org: string): Promise<ResolvedTemplate[]> {
    const cacheKey = `templates:${org}`;
    const cached = this.getCached<ResolvedTemplate[]>(cacheKey);
    if (cached) {
      return cached;
    }

    const searchResponse = await this.request<SearchResponse>(
      `/search/repositories?q=topic:${TEMPLATE_DISCOVERY_TOPIC}+org:${encodeURIComponent(
        org,
      )}&per_page=20`,
    );

    const manifestResults = await Promise.all(
      searchResponse.items.map(async repo => {
        try {
          const manifest = await this.getTemplateManifest(repo.full_name);
          return { repo, manifest };
        } catch {
          // Skip repos that don't have a valid manifest
          return { repo, manifest: null };
        }
      }),
    );

    const templates: ResolvedTemplate[] = [];
    for (const { repo, manifest } of manifestResults) {
      if (manifest) {
        const [owner, repoName] = repo.full_name.split('/');
        templates.push({
          owner,
          repo: repoName,
          fullName: repo.full_name,
          htmlUrl: repo.html_url,
          manifest,
        });
      }
    }

    // Cache with longer TTL for templates
    this.cache.set(cacheKey, {
      data: templates,
      expiresAt: Date.now() + TEMPLATE_CACHE_TTL_MS,
    });

    return templates;
  }

  /**
   * Fetch and parse the template manifest from a single repo.
   * Returns null if the file is missing or invalid.
   */
  async getTemplateManifest(
    repoFullName: string,
  ): Promise<TemplateManifest | null> {
    const cacheKey = `manifest:${repoFullName}`;
    const cached = this.getCached<TemplateManifest | null>(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const file = await this.getFile(repoFullName, TEMPLATE_MANIFEST_PATH);
    if (!file) {
      this.cache.set(cacheKey, {
        data: null,
        expiresAt: Date.now() + TEMPLATE_CACHE_TTL_MS,
      });
      return null;
    }

    try {
      const parsed = parseYaml(file.content) as TemplateManifest;

      const issues = validateManifestStructure(parsed);
      if (issues.length > 0) {
        return null;
      }

      this.cache.set(cacheKey, {
        data: parsed,
        expiresAt: Date.now() + TEMPLATE_CACHE_TTL_MS,
      });

      return parsed;
    } catch {
      return null;
    }
  }

  // ── ASDP records read from GitHub (journey spec §3.5, gate-review spec "The adjustable profile") ──

  /** The newest profile-change record issues (T11), newest first. */
  async listProfileChanges(repoFullName: string, perPage = 10) {
    const issues = await this.request<RecordIssue[]>(
      `/repos/${repoFullName}/issues?labels=${encodeURIComponent(
        PROFILE_CHANGE_LABEL,
      )}&state=all&sort=created&direction=desc&per_page=${perPage}`,
    );
    return issues.map(parseProfileChange);
  }

  /**
   * Changes merged to the default branch up to `until` (the Gate 1 merge),
   * oldest first: the quick-start work Gate 4 reviews (ASDP-SCO-11). Quick
   * start merges through pull requests only, so these are its commits.
   */
  async listQuickStartChanges(
    repoFullName: string,
    defaultBranch: string,
    until: string,
  ): Promise<QuickStartChange[]> {
    const pulls = await this.request<PullRequest[]>(
      `/repos/${repoFullName}/pulls?state=closed&base=${encodeURIComponent(
        defaultBranch,
      )}&sort=created&direction=asc&per_page=100`,
    );
    const end = Date.parse(until);
    return pulls
      .filter(
        (pull): pull is PullRequest & { merged_at: string } =>
          pull.merged_at !== null &&
          Date.parse(pull.merged_at) <= end &&
          !checkpointOfLabels(pull.labels),
      )
      .map(pull => ({
        number: pull.number,
        title: pull.title,
        author: pull.user?.login,
        mergedAt: pull.merged_at,
        url: pull.html_url,
      }))
      .sort((a, b) => a.mergedAt.localeCompare(b.mergedAt));
  }

  /**
   * A `roles` checkpoint pull request writing `.vivicta/asdp/roles.yaml`:
   * Settings → People and roles, and "Adopt review gates" for an existing
   * application (journey spec §9).
   */
  async openRolesPullRequest(
    repoFullName: string,
    rolesYaml: string,
    pullRequest: { title: string; body: string; branchSuffix: string },
  ): Promise<{ number: number; html_url: string }> {
    const repo = await this.getRepo(repoFullName);
    const branchName = `${CHECKPOINT_INFO.roles.branchPrefix}${pullRequest.branchSuffix}-${Date.now()}`;
    await this.createBranch(repoFullName, repo.default_branch, branchName);
    await this.upsertFile({
      repoFullName,
      branch: branchName,
      path: ROLES_PATH,
      content: rolesYaml,
      message: `chore(asdp): ${pullRequest.title.toLowerCase()}`,
    });
    const pull = await this.request<{ number: number; html_url: string }>(
      `/repos/${repoFullName}/pulls`,
      {
        method: 'POST',
        body: JSON.stringify({
          title: pullRequest.title,
          head: branchName,
          base: repo.default_branch,
          body: pullRequest.body,
        }),
      },
    );
    await this.request(`/repos/${repoFullName}/issues/${pull.number}/labels`, {
      method: 'POST',
      body: JSON.stringify({ labels: [checkpointLabel('roles')] }),
    });
    return pull;
  }

  /**
   * The resource providers and Azure features a template's platform needs,
   * from its infra/resource-providers.txt and azure-features.txt (one per
   * line, # comments). Empty lists when a file is missing.
   */
  async getTemplateAzureRequirements(
    repoFullName: string,
  ): Promise<AzureRequirements> {
    const cacheKey = `manifest:${repoFullName}:azure-requirements`;
    const cached = this.getCached<AzureRequirements>(cacheKey);
    if (cached) {
      return cached;
    }
    const [providers, features] = await Promise.all(
      [TEMPLATE_PROVIDERS_PATH, TEMPLATE_FEATURES_PATH].map(async path =>
        listedLines((await this.getFile(repoFullName, path))?.content ?? ''),
      ),
    );
    const requirements = { providers, features };
    this.cache.set(cacheKey, {
      data: requirements,
      expiresAt: Date.now() + TEMPLATE_CACHE_TTL_MS,
    });
    return requirements;
  }

  /** Invalidate the template discovery cache (e.g. after manual refresh). */
  invalidateTemplateCache() {
    for (const key of this.cache.keys()) {
      if (key.startsWith('templates:') || key.startsWith('manifest:')) {
        this.cache.delete(key);
      }
    }
  }
}
